import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEventManageContext } from './EventManagePage';
import { Save, Trash2, Copy, ExternalLink, AlertTriangle, X, Loader2, ListOrdered, ListMusic, RotateCcw, WifiOff, Check, Square, Download, Music, CheckCircle, Play, Clock, CheckCircle2, FileUp, QrCode, Shield, RefreshCw, Users, MapPin, Phone, Mail, User, Star, Smartphone, Printer, ArrowLeft, ChevronRight, Crown, ShieldCheck, FileMusic, Ticket, Brain } from 'lucide-react';
import { PEOPLES_PICK_HIDDEN } from '../../utils/featureFlags';
import { deleteEvent, updateEvent, endEvent, reopenEvent, getEventById, generateEventQrCode, generateQrCodeUrl, enableOnSiteVerification, disableOnSiteVerification, regenerateVerificationToken, generateVerificationQrCodeUrl, getEventsByDjId, computeEventStatus, getEventStartDate, eventStartAtMs, Event as DJEvent } from '../../services/events';
import { END_EVENT_CONFIRM_TEXT, REOPEN_EVENT_CONFIRM_TEXT } from '../../utils/endOfShow';
import { isClosedByShowStart, nextRequestsCloseAtMs, requestsCloseAtMsOf } from '../../utils/requestWindow';
import { useRequestWindow } from '../../hooks/useRequestWindow';
import { getVerifiedAttendeeCount } from '../../services/verification';
import { createTemplate } from '../../services/templates';
import { saveDJDefaults, getDJDefaults, SYSTEM_DEFAULTS } from '../../services/djDefaults';
import { getRequestsByEventId, importSongsFromPastEvent, subscribeToRequestsByEventId, SongRequest, SmartImportResult } from '../../services/requests';
import { setRequestLimit, requestLimitRefusalFloor } from '../../services/requestLimit';
import { librarySongOf, saveSongLinks, type SaveSongLinkEntry } from '../../services/songMatchMemory';
import { parseMusicLink } from '../../utils/songLinks';
import LibraryQueueModal from '../../components/dj/LibraryQueueModal';
import { createVipSession, getVipSession, deleteVipSession, generateVipQrUrl, getVipPageUrl } from '../../services/vipTrigger';
import { CoApprovalDjSettings } from '../../components/dj/CoApprovalDjSettings';
import { createCoApprovalSession, getCoApprovalSession, deleteCoApprovalSession, generateCoApprovalQrUrl, getCoApprovalPageUrl, saveCoApproverEmail, type CoApprovalSession, type CoApproverLinkEmailStatus } from '../../services/coApproval';
import { FirebaseError } from 'firebase/app';
import { useAuth } from '../../context/AuthContext';
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning';
import UnsavedChangesModal from '../../components/UnsavedChangesModal';
import QrPrintModal from '../../components/QrPrintModal';
import SaveBar from '../../components/SaveBar';
import toast from 'react-hot-toast';
import { omitLegacyTokens } from '../../utils/shareLinks';
import {
  REQUEST_LIMITS_OFF_VALUE,
  MAX_REQUEST_LIMIT,
  highestActiveRequestCount,
  isRequestLimitLocked,
  requestLimitFloor,
  requestLimitFloorOf,
  requestLimitFloorText,
  requestLimitCorrectedSaveText,
  requestLimitRefusalText,
  startingRequestLimit,
} from '../../utils/requestLimitLock';
import { isPlayModeLocked } from '../../utils/playModeLock';
import {
  DEFAULT_UPVOTE_OVERRIDE_PERCENT,
  DEFAULT_VOTE_OUT_THRESHOLD,
  MIN_PARTICIPANTS,
  RETURN_GAP_POINTS,
  UPVOTE_OVERRIDE_OPTIONS,
  VOTE_OUT_THRESHOLD_OPTIONS,
  isVoteRulesLocked,
  voteRuleValuesEqual,
  voteRuleValuesOf,
  type VoteRuleValues,
} from '../../utils/voteOut';
import { duplicateEventNameMessage, findDuplicateEventName, normalizeEventName } from '../../utils/eventNames';
import { partitionForImport } from '../../utils/importPartition';
import { LEGACY_PLAY_MODE, playModeOf, type PlayMode } from '../../services/playModes';
import { PlayModePicker } from '../../components/dj/PlayModePicker';

// Normalize a stored datetime string to local format for datetime-local inputs.
// Old data may have UTC 'Z' suffix; new data is stored as local.
function toLocalDatetimeString(isoStr: string): string {
  if (!isoStr) return '';
  // If it ends with Z or has timezone offset, convert to local
  if (isoStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoStr)) {
    const d = new Date(isoStr);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  return isoStr;
}

// Offline checklist item type
interface OfflineChecklistItem {
  id: string;
  label: string;
  description: string;
  checked: boolean;
}

// Get/set offline checklist from localStorage
const getOfflineChecklist = (eventId: string): OfflineChecklistItem[] => {
  const key = `votebeats_offline_checklist_${eventId}`;
  const stored = localStorage.getItem(key);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      return getDefaultChecklist();
    }
  }
  return getDefaultChecklist();
};

const saveOfflineChecklist = (eventId: string, checklist: OfflineChecklistItem[]) => {
  const key = `votebeats_offline_checklist_${eventId}`;
  localStorage.setItem(key, JSON.stringify(checklist));
};

const getDefaultChecklist = (): OfflineChecklistItem[] => [
  { id: 'close_voting', label: 'Close Voting', description: 'Close voting window so attendees know the queue is finalized', checked: false },
  { id: 'review_queue', label: 'Review Queue', description: 'Review all songs and remove any you don\'t want to play', checked: false },
  // { id: 'export_spotify', label: 'Export Playlist to Spotify', description: 'Create a Spotify playlist with your finalized queue', checked: false }, // Hidden: Spotify API integration paused
  // { id: 'download_offline', label: 'Download for Offline Playback', description: 'In Spotify, download the playlist for offline listening', checked: false }, // Hidden: Spotify API integration paused
  { id: 'enable_offline', label: 'Enable Offline Mode', description: 'Enable offline mode in VoteBeats Bridge', checked: false },
  { id: 'download_backup', label: 'Download Backup', description: 'Export your queue as CSV/PDF as a backup reference', checked: false },
];

// Queue mode options
const QUEUE_MODES = [
  { value: 'standard', label: 'Standard Voting', description: 'Simple upvote system' },
  { value: 'ranked', label: 'Ranked Choice', description: 'Copeland pairwise scoring' },
  { value: 'fifo', label: 'First In, First Out', description: 'No voting, order by submission' },
];

// Default settings values
const DEFAULT_SETTINGS = {
  isActive: true,
  closeRequestsAtStart: false,
  allowTips: true,
  maxQueueSize: 50,
  autoApprove: false,
  pendingEmailAlerts: true,
  queueMode: 'standard' as const,
};

export default function EventSettingsPage() {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { event, setEvent } = useEventManageContext();
  const { user } = useAuth();
  const isLocked = event?.locked ?? false;

  const [name, setName] = useState(event?.name || 'Saturday Night Party');
  const [nameError, setNameError] = useState<string | null>(null);
  // Event info fields (F10, F11, F12)
  const [eventDate, setEventDate] = useState(event?.date || '');
  const [startTime, setStartTime] = useState(event?.startTime || '');
  const [endTime, setEndTime] = useState(event?.endTime || '');
  const [location, setLocation] = useState(event?.location || '');
  const [address, setAddress] = useState(event?.address || '');
  const [description, setDescription] = useState(event?.description || '');
  const [contactName, setContactName] = useState(event?.contactInfo?.name || '');
  const [contactPhone, setContactPhone] = useState(event?.contactInfo?.phone || '');
  const [contactEmail, setContactEmail] = useState(event?.contactInfo?.email || '');

  // Save as template modal state
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateSaveLoading, setTemplateSaveLoading] = useState(false);
  const [isActive, setIsActive] = useState(event?.settings?.requestsOpen ?? true);
  const [closeRequestsAtStart, setCloseRequestsAtStart] = useState(event?.settings?.closeRequestsAtStart ?? false);
  const [allowTips, setAllowTips] = useState(event?.settings?.allowTips ?? true);
  const [maxQueueSize, setMaxQueueSize] = useState(event?.settings?.maxQueueSize ?? 50);
  const [autoApprove, setAutoApprove] = useState(event?.settings?.autoApprove ?? false);
  const [pendingEmailAlerts, setPendingEmailAlerts] = useState(event?.settings?.pendingEmailAlerts ?? true);
  const [queueMode, setQueueMode] = useState<'standard' | 'ranked' | 'fifo'>(event?.settings?.queueMode || 'standard');
  const [downvotingEnabled, setDownvotingEnabled] = useState(event?.settings?.downvotingEnabled || false);
  const [voteOutEnabled, setVoteOutEnabled] = useState(event?.settings?.voteOutEnabled ?? true);
  const [voteOutThresholdPercent, setVoteOutThresholdPercent] = useState(event?.settings?.voteOutThresholdPercent ?? DEFAULT_VOTE_OUT_THRESHOLD);
  const [upvoteOverrideEnabled, setUpvoteOverrideEnabled] = useState(event?.settings?.upvoteOverrideEnabled ?? false);
  const [upvoteOverridePercent, setUpvoteOverridePercent] = useState(event?.settings?.upvoteOverridePercent ?? DEFAULT_UPVOTE_OVERRIDE_PERCENT);
  const [allowOverlap, setAllowOverlap] = useState(event?.settings?.allowOverlap || false);
  const [crossfadeSeconds, setCrossfadeSeconds] = useState(event?.settings?.crossfadeSeconds ?? 0);
  const [rankSize, setRankSize] = useState(event?.settings?.rankSize ?? event?.settings?.rankingMaxSongs ?? 5);
  const [rankingStalenessMinutes, setRankingStalenessMinutes] = useState(event?.settings?.rankingStalenessMinutes ?? 0);
  const [rankingMinParticipants, setRankingMinParticipants] = useState(event?.settings?.rankingMinParticipants ?? 3);
  const [blockExplicit, setBlockExplicit] = useState(event?.settings?.blockExplicit || event?.settings?.filterExplicit || false);
  const [warnExplicit, setWarnExplicit] = useState(event?.settings?.warnExplicit ?? true);
  const [autoRejectBlocked, setAutoRejectBlocked] = useState(event?.settings?.autoRejectBlocked ?? false);
  const [allowAttendeeFlagging, setAllowAttendeeFlagging] = useState(event?.settings?.allowAttendeeFlagging ?? false);
  const [doomVotingEnabled, setDoomVotingEnabled] = useState(event?.settings?.doomVotingEnabled ?? false);
  const [doomVotingMaxVotes, setDoomVotingMaxVotes] = useState(event?.settings?.doomVotingMaxVotes ?? 10);
  const [doomVotingActiveMinutes, setDoomVotingActiveMinutes] = useState(event?.settings?.doomVotingActiveMinutes ?? 3);
  const [doomVotingRestMinutes, setDoomVotingRestMinutes] = useState(event?.settings?.doomVotingRestMinutes ?? 5);
  const [superRequestEnabled, setSuperRequestEnabled] = useState(event?.settings?.superRequestEnabled ?? false);
  const [superRequestThresholdType, setSuperRequestThresholdType] = useState<'count' | 'percentage'>(event?.settings?.superRequestThresholdType ?? 'count');
  const [superRequestThresholdCount, setSuperRequestThresholdCount] = useState(event?.settings?.superRequestThresholdCount ?? 5);
  const [superRequestThresholdPercent, setSuperRequestThresholdPercent] = useState(event?.settings?.superRequestThresholdPercent ?? 10);
  const [maxNominationsPerUser, setMaxNominationsPerUser] = useState(event?.settings?.maxNominationsPerUser ?? 0);
  const [eventStatus, setEventStatus] = useState<'upcoming' | 'active' | 'completed'>(event?.status || 'upcoming');
  const [statusLoading, setStatusLoading] = useState(false);
  // Voting close time state
  const [votingCloseTime, setVotingCloseTime] = useState(toLocalDatetimeString(event?.settings?.votingCloseTime || ''));
  // Voting open time state
  const [votingOpenTime, setVotingOpenTime] = useState(toLocalDatetimeString(event?.settings?.votingOpenTime || ''));
  const [votingScheduledToOpen, setVotingScheduledToOpen] = useState(event?.settings?.votingScheduledToOpen || false);

  // Request limits state
  const [requestLimitsEnabled, setRequestLimitsEnabled] = useState(
    (event?.settings?.cooldownMinutes ?? 5) > 0 || (event?.settings?.maxRequestsPerUser ?? 5) < 50
  );
  const [cooldownMinutes, setCooldownMinutes] = useState(event?.settings?.cooldownMinutes ?? 5);
  const [maxRequestsPerUser, setMaxRequestsPerUser] = useState(event?.settings?.maxRequestsPerUser ?? 5);
  const [refusedLimitFloor, setRefusedLimitFloor] = useState<number | null>(null);
  const savedLimitFloor = refusedLimitFloor ?? requestLimitFloor(event);
  const limitLocked = isRequestLimitLocked(event) || refusedLimitFloor !== null;
  const [highestActiveCount, setHighestActiveCount] = useState<number | null>(null);
  const activeLimitFloor = limitLocked && highestActiveCount !== null ? requestLimitFloorOf(highestActiveCount) : null;
  const [limitFloorWarning, setLimitFloorWarning] = useState(false);
  const [correctedFromLimit, setCorrectedFromLimit] = useState<number | null>(null);
  const eventId = event?.id;
  const eventDjId = event?.djId;
  useEffect(() => {
    if (!eventId || !eventDjId || !limitLocked) {
      setHighestActiveCount(null);
      return;
    }
    return subscribeToRequestsByEventId(
      eventId,
      (requests) => setHighestActiveCount(highestActiveRequestCount(requests, eventDjId)),
      (err) => {
        console.error('Request limit floor listener failed:', err);
        setHighestActiveCount(null);
      },
    );
  }, [eventId, eventDjId, limitLocked]);
  const [overflowEnabled, setOverflowEnabled] = useState(event?.settings?.overflowEnabled ?? false);
  const [hideRankingInfo, setHideRankingInfo] = useState(event?.settings?.hideRankingInfo ?? false);
  const [lotteryEnabled, setLotteryEnabled] = useState(event?.settings?.lotteryEnabled ?? false);
  const [lotteryAllowRepeatWinners, setLotteryAllowRepeatWinners] = useState(event?.settings?.lotteryAllowRepeatWinners ?? true);
  const [triviaEnabled, setTriviaEnabled] = useState(event?.settings?.triviaEnabled ?? false);

  // Play mode (ITER-121) — locked once the event has its first non-practice request
  const [playMode, setPlayMode] = useState<PlayMode>(playModeOf(event).id);
  const [playModeLockKnown, setPlayModeLockKnown] = useState(false);
  const savedPlayMode = playModeOf(event);
  const playModeLocked = isPlayModeLocked(event) || playModeLockKnown;

  // Crowd voting rules (ITER-144 / ITER-075) — lock with the play mode
  const [voteRulesLockKnown, setVoteRulesLockKnown] = useState(false);
  const voteRulesLocked = isVoteRulesLocked(event) || voteRulesLockKnown;
  const fifoBlockedByVoteLock = voteRulesLocked && downvotingEnabled && event?.settings?.queueMode !== 'fifo';

  // Queue visibility settings
  const [preEventQueueView, setPreEventQueueView] = useState<'full' | 'own_only' | 'blind'>(event?.settings?.preEventQueueView || 'full');
  const [postCloseQueueView, setPostCloseQueueView] = useState<'show' | 'hide'>(event?.settings?.postCloseQueueView || 'show');
  const [duringEventQueueView, setDuringEventQueueView] = useState<'now_and_upcoming' | 'now_only' | 'hide'>(event?.settings?.duringEventQueueView || 'now_and_upcoming');

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Close voting confirmation modal state
  const [showCloseVotingModal, setShowCloseVotingModal] = useState(false);

  // Content filter change confirmation modal state
  const [showContentFilterModal, setShowContentFilterModal] = useState(false);
  const [pendingBlockExplicitValue, setPendingBlockExplicitValue] = useState(false);

  // Offline checklist state
  const [offlineChecklist, setOfflineChecklist] = useState<OfflineChecklistItem[]>([]);

  // QR Code state
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(event?.qrCodeUrl || null);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showQrPrint, setShowQrPrint] = useState(false);

  // On-site verification state
  const [onSiteVerificationEnabled, setOnSiteVerificationEnabled] = useState(event?.settings?.onSiteVerificationEnabled || false);
  const [verificationToken, setVerificationToken] = useState(event?.settings?.onSiteVerificationToken || '');
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verifiedCount, setVerifiedCount] = useState(0);
  const [showEnableVerificationModal, setShowEnableVerificationModal] = useState(false);
  const [showRegenerateTokenModal, setShowRegenerateTokenModal] = useState(false);
  const [showVerificationQrModal, setShowVerificationQrModal] = useState(false);

  // VIP Trigger state
  const [vipTriggerEnabled, setVipTriggerEnabled] = useState(event?.settings?.vipTriggerEnabled || false);
  const [vipTriggerToken, setVipTriggerToken] = useState('');
  const [vipLoading, setVipLoading] = useState(false);
  const [showVipQrModal, setShowVipQrModal] = useState(false);

  // Co-Approval state
  const [coApprovalEnabled, setCoApprovalEnabled] = useState(event?.settings?.coApprovalEnabled || false);
  const [coApprovalToken, setCoApprovalToken] = useState('');
  const [coApprovalLoading, setCoApprovalLoading] = useState(false);
  const [showCoApprovalQrModal, setShowCoApprovalQrModal] = useState(false);
  const [coApproverEmailDraft, setCoApproverEmailDraft] = useState('');
  const [coApproverEmailSaved, setCoApproverEmailSaved] = useState<string | null>(null);
  const [coApproverAlertsEnabled, setCoApproverAlertsEnabled] = useState(true);
  const [coApproverLinkEmailStatus, setCoApproverLinkEmailStatus] = useState<CoApproverLinkEmailStatus | null>(null);
  const [coApproverEmailSending, setCoApproverEmailSending] = useState(false);

  // Local Files state
  const [localFilesEnabled, setLocalFilesEnabled] = useState(event?.settings?.localFilesEnabled || false);

  // Cross-event match propagation
  const [autoApplyMatch, setAutoApplyMatch] = useState(event?.settings?.autoApplyMatchFromOtherEvents || false);

  // Import from Past Event modal state
  const [showImportModal, setShowImportModal] = useState(false);
  // Library Queue modal state
  const [showLibraryModal, setShowLibraryModal] = useState(false);
  const [importStep, setImportStep] = useState<'pick-event' | 'pick-songs'>('pick-event');
  const [pastEvents, setPastEvents] = useState<DJEvent[]>([]);
  const [pastEventsLoading, setPastEventsLoading] = useState(false);
  const [selectedPastEvent, setSelectedPastEvent] = useState<DJEvent | null>(null);
  const [pastEventSongs, setPastEventSongs] = useState<SongRequest[]>([]);
  const [pastSongsLoading, setPastSongsLoading] = useState(false);
  const [selectedSongIds, setSelectedSongIds] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<SmartImportResult | null>(null);

  // NFC tag writing
  const nfcSupported = 'NDEFReader' in window;
  const [nfcWriting, setNfcWriting] = useState(false);
  const [nfcWritingType, setNfcWritingType] = useState<'event' | 'verification' | null>(null);
  const [nfcLockTag, setNfcLockTag] = useState(false);
  const nfcAbortRef = useRef<AbortController | null>(null);

  // Track initial values to detect unsaved changes
  const initialValues = useMemo(() => ({
    name: event?.name || 'Saturday Night Party',
    eventDate: event?.date || '',
    startTime: event?.startTime || '',
    endTime: event?.endTime || '',
    location: event?.location || '',
    address: event?.address || '',
    description: event?.description || '',
    contactName: event?.contactInfo?.name || '',
    contactPhone: event?.contactInfo?.phone || '',
    contactEmail: event?.contactInfo?.email || '',
    isActive: event?.settings?.requestsOpen ?? true,
    closeRequestsAtStart: event?.settings?.closeRequestsAtStart ?? false,
    allowTips: event?.settings?.allowTips ?? true,
    maxQueueSize: event?.settings?.maxQueueSize ?? 50,
    autoApprove: event?.settings?.autoApprove ?? false,
    pendingEmailAlerts: event?.settings?.pendingEmailAlerts ?? true,
    queueMode: event?.settings?.queueMode || 'standard',
    blockExplicit: event?.settings?.blockExplicit || event?.settings?.filterExplicit || false,
    warnExplicit: event?.settings?.warnExplicit ?? true,
    autoRejectBlocked: event?.settings?.autoRejectBlocked ?? false,
    allowAttendeeFlagging: event?.settings?.allowAttendeeFlagging ?? false,
    doomVotingEnabled: event?.settings?.doomVotingEnabled ?? false,
    doomVotingMaxVotes: event?.settings?.doomVotingMaxVotes ?? 10,
    doomVotingActiveMinutes: event?.settings?.doomVotingActiveMinutes ?? 3,
    doomVotingRestMinutes: event?.settings?.doomVotingRestMinutes ?? 5,
    superRequestEnabled: event?.settings?.superRequestEnabled ?? false,
    superRequestThresholdType: event?.settings?.superRequestThresholdType ?? 'count',
    superRequestThresholdCount: event?.settings?.superRequestThresholdCount ?? 5,
    superRequestThresholdPercent: event?.settings?.superRequestThresholdPercent ?? 10,
    maxNominationsPerUser: event?.settings?.maxNominationsPerUser ?? 0,
    cooldownMinutes: event?.settings?.cooldownMinutes ?? 5,
    maxRequestsPerUser: event?.settings?.maxRequestsPerUser ?? 5,
    playMode: playModeOf(event).id,
    allowOverlap: event?.settings?.allowOverlap || false,
    crossfadeSeconds: event?.settings?.crossfadeSeconds ?? 0,
    rankSize: event?.settings?.rankSize ?? event?.settings?.rankingMaxSongs ?? 5,
    rankingStalenessMinutes: event?.settings?.rankingStalenessMinutes ?? 0,
    overflowEnabled: event?.settings?.overflowEnabled ?? false,
    hideRankingInfo: event?.settings?.hideRankingInfo ?? false,
    lotteryEnabled: event?.settings?.lotteryEnabled ?? false,
    lotteryAllowRepeatWinners: event?.settings?.lotteryAllowRepeatWinners ?? true,
    triviaEnabled: event?.settings?.triviaEnabled ?? false,
    localFilesEnabled: event?.settings?.localFilesEnabled || false,
    autoApplyMatch: event?.settings?.autoApplyMatchFromOtherEvents || false,
    requestLimitsEnabled: (event?.settings?.cooldownMinutes ?? 5) > 0 || (event?.settings?.maxRequestsPerUser ?? 5) < 50,
    votingCloseTime: toLocalDatetimeString(event?.settings?.votingCloseTime || ''),
    votingOpenTime: toLocalDatetimeString(event?.settings?.votingOpenTime || ''),
    votingScheduledToOpen: event?.settings?.votingScheduledToOpen || false,
    downvotingEnabled: event?.settings?.downvotingEnabled || false,
    voteOutEnabled: event?.settings?.voteOutEnabled ?? true,
    voteOutThresholdPercent: event?.settings?.voteOutThresholdPercent ?? DEFAULT_VOTE_OUT_THRESHOLD,
    upvoteOverrideEnabled: event?.settings?.upvoteOverrideEnabled ?? false,
    upvoteOverridePercent: event?.settings?.upvoteOverridePercent ?? DEFAULT_UPVOTE_OVERRIDE_PERCENT,
    rankingMinParticipants: event?.settings?.rankingMinParticipants ?? 3,
    preEventQueueView: event?.settings?.preEventQueueView || 'full',
    postCloseQueueView: event?.settings?.postCloseQueueView || 'show',
    duringEventQueueView: event?.settings?.duringEventQueueView || 'now_and_upcoming',
  }), [event]);

  function resetUncheckedFields() {
    setRequestLimitsEnabled(initialValues.requestLimitsEnabled);
    setVotingCloseTime(initialValues.votingCloseTime);
    setVotingOpenTime(initialValues.votingOpenTime);
    setVotingScheduledToOpen(initialValues.votingScheduledToOpen);
    setDownvotingEnabled(initialValues.downvotingEnabled);
    setVoteOutEnabled(initialValues.voteOutEnabled);
    setVoteOutThresholdPercent(initialValues.voteOutThresholdPercent);
    setUpvoteOverrideEnabled(initialValues.upvoteOverrideEnabled);
    setUpvoteOverridePercent(initialValues.upvoteOverridePercent);
    setRankingMinParticipants(initialValues.rankingMinParticipants);
    setPreEventQueueView(initialValues.preEventQueueView);
    setPostCloseQueueView(initialValues.postCloseQueueView);
    setDuringEventQueueView(initialValues.duringEventQueueView);
  }

  // Sync form fields when event data changes externally (e.g., QueuePage inline edits)
  const prevEventRef = useRef(event);
  useEffect(() => {
    // Skip if this is the initial render or event hasn't actually changed
    if (prevEventRef.current === event) return;
    prevEventRef.current = event;
    if (!event) return;

    // Reset all form fields to match the updated event
    setName(initialValues.name);
    setEventDate(initialValues.eventDate);
    setStartTime(initialValues.startTime);
    setEndTime(initialValues.endTime);
    setLocation(initialValues.location);
    setAddress(initialValues.address);
    setDescription(initialValues.description);
    setContactName(initialValues.contactName);
    setContactPhone(initialValues.contactPhone);
    setContactEmail(initialValues.contactEmail);
    setIsActive(initialValues.isActive);
    setCloseRequestsAtStart(initialValues.closeRequestsAtStart);
    setAllowTips(initialValues.allowTips);
    setMaxQueueSize(initialValues.maxQueueSize);
    setAutoApprove(initialValues.autoApprove);
    setPendingEmailAlerts(initialValues.pendingEmailAlerts);
    setQueueMode(initialValues.queueMode);
    setBlockExplicit(initialValues.blockExplicit);
    setWarnExplicit(initialValues.warnExplicit);
    setAutoRejectBlocked(initialValues.autoRejectBlocked);
    setAllowAttendeeFlagging(initialValues.allowAttendeeFlagging);
    setDoomVotingEnabled(initialValues.doomVotingEnabled);
    setDoomVotingMaxVotes(initialValues.doomVotingMaxVotes);
    setDoomVotingActiveMinutes(initialValues.doomVotingActiveMinutes);
    setDoomVotingRestMinutes(initialValues.doomVotingRestMinutes);
    setSuperRequestEnabled(initialValues.superRequestEnabled);
    setSuperRequestThresholdType(initialValues.superRequestThresholdType);
    setSuperRequestThresholdCount(initialValues.superRequestThresholdCount);
    setSuperRequestThresholdPercent(initialValues.superRequestThresholdPercent);
    setMaxNominationsPerUser(initialValues.maxNominationsPerUser);
    setCooldownMinutes(initialValues.cooldownMinutes);
    setMaxRequestsPerUser(initialValues.maxRequestsPerUser);
    setPlayMode(initialValues.playMode);
    setAllowOverlap(initialValues.allowOverlap);
    setCrossfadeSeconds(initialValues.crossfadeSeconds);
    setRankSize(initialValues.rankSize);
    setRankingStalenessMinutes(initialValues.rankingStalenessMinutes);
    setOverflowEnabled(initialValues.overflowEnabled);
    setHideRankingInfo(initialValues.hideRankingInfo);
    setLotteryEnabled(initialValues.lotteryEnabled);
    setLotteryAllowRepeatWinners(initialValues.lotteryAllowRepeatWinners);
    setTriviaEnabled(initialValues.triviaEnabled);
    setLocalFilesEnabled(initialValues.localFilesEnabled);
    setAutoApplyMatch(initialValues.autoApplyMatch);
    setEventStatus(event.status || 'upcoming');
    resetUncheckedFields();
  }, [event, initialValues]);

  // Track manual status overrides — don't auto-revert if DJ manually changed status
  const manualStatusOverrideRef = useRef(false);

  // Auto-update event status based on time (every 60 seconds)
  useEffect(() => {
    if (!event || eventStatus === 'completed') return;

    const checkStatus = async () => {
      if (manualStatusOverrideRef.current) return;

      const computedStatus = computeEventStatus(event);
      if (computedStatus !== eventStatus) {
        // For completed auto-transition, also check that queue is empty
        if (computedStatus === 'completed') {
          try {
            const requests = await getRequestsByEventId(event.id);
            const activeQueue = requests.filter(r => r.status === 'queued' || r.status === 'nowPlaying');
            if (activeQueue.length > 0) return; // Don't auto-complete while songs are still queued
          } catch {
            return; // Skip on error
          }
        }

        try {
          await updateEvent(event.id, { status: computedStatus });
          setEventStatus(computedStatus);
          setEvent(prev => prev ? { ...prev, status: computedStatus } : prev);
          const labels = { upcoming: 'Upcoming', active: 'Active', completed: 'Completed' };
          toast.success(`Event status auto-updated to ${labels[computedStatus]}`, { id: 'auto-status-update' });

        } catch {
          // Silent fail — will retry on next interval
        }
      }
    };

    // Check immediately on mount
    checkStatus();

    // Then check every 60 seconds
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, [event, eventStatus, user]);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return (
      name !== initialValues.name ||
      eventDate !== initialValues.eventDate ||
      startTime !== initialValues.startTime ||
      endTime !== initialValues.endTime ||
      location !== initialValues.location ||
      address !== initialValues.address ||
      description !== initialValues.description ||
      contactName !== initialValues.contactName ||
      contactPhone !== initialValues.contactPhone ||
      contactEmail !== initialValues.contactEmail ||
      isActive !== initialValues.isActive ||
      closeRequestsAtStart !== initialValues.closeRequestsAtStart ||
      allowTips !== initialValues.allowTips ||
      maxQueueSize !== initialValues.maxQueueSize ||
      autoApprove !== initialValues.autoApprove ||
      pendingEmailAlerts !== initialValues.pendingEmailAlerts ||
      queueMode !== initialValues.queueMode ||
      blockExplicit !== initialValues.blockExplicit ||
      warnExplicit !== initialValues.warnExplicit ||
      autoRejectBlocked !== initialValues.autoRejectBlocked ||
      allowAttendeeFlagging !== initialValues.allowAttendeeFlagging ||
      doomVotingEnabled !== initialValues.doomVotingEnabled ||
      doomVotingMaxVotes !== initialValues.doomVotingMaxVotes ||
      doomVotingActiveMinutes !== initialValues.doomVotingActiveMinutes ||
      doomVotingRestMinutes !== initialValues.doomVotingRestMinutes ||
      superRequestEnabled !== initialValues.superRequestEnabled ||
      superRequestThresholdType !== initialValues.superRequestThresholdType ||
      superRequestThresholdCount !== initialValues.superRequestThresholdCount ||
      superRequestThresholdPercent !== initialValues.superRequestThresholdPercent ||
      maxNominationsPerUser !== initialValues.maxNominationsPerUser ||
      cooldownMinutes !== initialValues.cooldownMinutes ||
      maxRequestsPerUser !== initialValues.maxRequestsPerUser ||
      playMode !== initialValues.playMode ||
      allowOverlap !== initialValues.allowOverlap ||
      crossfadeSeconds !== initialValues.crossfadeSeconds ||
      rankSize !== initialValues.rankSize ||
      rankingStalenessMinutes !== initialValues.rankingStalenessMinutes ||
      overflowEnabled !== initialValues.overflowEnabled ||
      hideRankingInfo !== initialValues.hideRankingInfo ||
      lotteryEnabled !== initialValues.lotteryEnabled ||
      lotteryAllowRepeatWinners !== initialValues.lotteryAllowRepeatWinners ||
      triviaEnabled !== initialValues.triviaEnabled ||
      localFilesEnabled !== initialValues.localFilesEnabled ||
      autoApplyMatch !== initialValues.autoApplyMatch ||
      requestLimitsEnabled !== initialValues.requestLimitsEnabled ||
      votingCloseTime !== initialValues.votingCloseTime ||
      votingOpenTime !== initialValues.votingOpenTime ||
      votingScheduledToOpen !== initialValues.votingScheduledToOpen ||
      downvotingEnabled !== initialValues.downvotingEnabled ||
      voteOutEnabled !== initialValues.voteOutEnabled ||
      voteOutThresholdPercent !== initialValues.voteOutThresholdPercent ||
      upvoteOverrideEnabled !== initialValues.upvoteOverrideEnabled ||
      upvoteOverridePercent !== initialValues.upvoteOverridePercent ||
      rankingMinParticipants !== initialValues.rankingMinParticipants ||
      preEventQueueView !== initialValues.preEventQueueView ||
      postCloseQueueView !== initialValues.postCloseQueueView ||
      duringEventQueueView !== initialValues.duringEventQueueView
    );
  }, [name, eventDate, startTime, endTime, location, address, description, contactName, contactPhone, contactEmail, isActive, closeRequestsAtStart, allowTips, maxQueueSize, autoApprove, pendingEmailAlerts, queueMode, blockExplicit, warnExplicit, autoRejectBlocked, doomVotingEnabled, doomVotingMaxVotes, doomVotingActiveMinutes, doomVotingRestMinutes, superRequestEnabled, superRequestThresholdType, superRequestThresholdCount, superRequestThresholdPercent, maxNominationsPerUser, cooldownMinutes, maxRequestsPerUser, playMode, allowOverlap, crossfadeSeconds, rankSize, rankingStalenessMinutes, overflowEnabled, hideRankingInfo, lotteryEnabled, lotteryAllowRepeatWinners, triviaEnabled, localFilesEnabled, autoApplyMatch, requestLimitsEnabled, votingCloseTime, votingOpenTime, votingScheduledToOpen, downvotingEnabled, voteOutEnabled, voteOutThresholdPercent, upvoteOverrideEnabled, upvoteOverridePercent, rankingMinParticipants, preEventQueueView, postCloseQueueView, duringEventQueueView, initialValues]);

  // Unsaved changes warning hook
  const {
    showModal: showUnsavedModal,
    proceed: proceedNavigation,
    cancel: cancelNavigation,
  } = useUnsavedChangesWarning(hasUnsavedChanges, {
    message: 'You have unsaved changes to your event settings. Are you sure you want to leave without saving?',
  });

  // Lock body scroll when any modal is open
  useEffect(() => {
    const anyModalOpen = showDeleteModal || showCloseVotingModal || showContentFilterModal || showSaveTemplateModal || showQrModal || showQrPrint || showUnsavedModal || showEnableVerificationModal || showRegenerateTokenModal || showVerificationQrModal || showImportModal;
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showDeleteModal, showCloseVotingModal, showContentFilterModal, showSaveTemplateModal, showQrModal, showQrPrint, showUnsavedModal, showEnableVerificationModal, showRegenerateTokenModal, showVerificationQrModal, showImportModal]);

  // Load offline checklist on mount
  useEffect(() => {
    if (event?.id) {
      setOfflineChecklist(getOfflineChecklist(event.id));
    }
  }, [event?.id]);

  // Initialize QR code on load if not already generated
  useEffect(() => {
    if (event?.eventUrl && !qrCodeUrl) {
      // Auto-generate QR code URL on first load
      const url = generateQrCodeUrl(event.eventUrl);
      setQrCodeUrl(url);
    }
  }, [event?.eventUrl, qrCodeUrl]);

  // Handle QR code generation
  const handleGenerateQrCode = async () => {
    if (!event?.id) return;

    setQrCodeLoading(true);
    try {
      const url = await generateEventQrCode(event.id);
      setQrCodeUrl(url);
      toast.success('QR code generated successfully!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to generate QR code');
    } finally {
      setQrCodeLoading(false);
    }
  };

  // Download QR code
  const handleDownloadQrCode = async () => {
    if (!qrCodeUrl) return;

    try {
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `votebeats-${event?.eventUrl || 'event'}-qr.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('QR code downloaded!');
    } catch {
      toast.error('Failed to download QR code');
    }
  };

  // Fetch verified attendee count when verification is enabled
  useEffect(() => {
    if (event?.id && onSiteVerificationEnabled) {
      getVerifiedAttendeeCount(event.id).then(setVerifiedCount).catch(() => {});
    }
  }, [event?.id, onSiteVerificationEnabled]);

  // Handle enabling on-site verification
  const handleEnableVerification = async () => {
    if (!event?.id) return;
    setVerificationLoading(true);
    try {
      const token = await enableOnSiteVerification(event.id);
      setVerificationToken(token);
      setOnSiteVerificationEnabled(true);
      setVerifiedCount(0);
      setShowEnableVerificationModal(false);
      toast.success('On-site verification enabled!');
    } catch {
      toast.error('Failed to enable on-site verification');
    } finally {
      setVerificationLoading(false);
    }
  };

  // Handle disabling on-site verification
  const handleDisableVerification = async () => {
    if (!event?.id) return;
    setVerificationLoading(true);
    try {
      await disableOnSiteVerification(event.id);
      setOnSiteVerificationEnabled(false);
      toast.success('On-site verification disabled. All attendees can now participate.');
    } catch {
      toast.error('Failed to disable on-site verification');
    } finally {
      setVerificationLoading(false);
    }
  };

  // Handle regenerating verification token
  const handleRegenerateToken = async () => {
    if (!event?.id) return;
    setVerificationLoading(true);
    try {
      const token = await regenerateVerificationToken(event.id);
      setVerificationToken(token);
      setVerifiedCount(0);
      setShowRegenerateTokenModal(false);
      toast.success('Verification code regenerated. All attendees must re-scan.');
    } catch {
      toast.error('Failed to regenerate verification code');
    } finally {
      setVerificationLoading(false);
    }
  };

  // Get verification QR code URL
  const verificationQrUrl = onSiteVerificationEnabled && verificationToken && event?.eventUrl
    ? generateVerificationQrCodeUrl(event.eventUrl, verificationToken)
    : null;

  // Get verification share URL
  const verificationShareUrl = onSiteVerificationEnabled && verificationToken && event?.eventUrl
    ? `${window.location.origin}/e/${event.eventUrl}?verify=${verificationToken}`
    : '';

  // Copy verification link
  const copyVerificationLink = () => {
    if (verificationShareUrl) {
      navigator.clipboard.writeText(verificationShareUrl);
      toast.success('Verification link copied!');
    }
  };

  // Download verification QR code
  const handleDownloadVerificationQr = async () => {
    if (!verificationQrUrl) return;
    try {
      const response = await fetch(verificationQrUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `votebeats-${event?.eventUrl || 'event'}-verification-qr.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Verification QR code downloaded!');
    } catch {
      toast.error('Failed to download verification QR code');
    }
  };

  useEffect(() => {
    if (!event?.id || !user?.uid || !vipTriggerEnabled) return;
    getVipSession(event.id, user.uid)
      .then(session => setVipTriggerToken(session?.token ?? ''))
      .catch(loadError => {
        console.error('Failed to load VIP link', loadError);
        toast.error('Failed to load VIP link');
      });
  }, [event?.id, user?.uid, vipTriggerEnabled]);

  useEffect(() => {
    if (!event?.id || !user?.uid || !coApprovalEnabled) return;
    getCoApprovalSession(event.id, user.uid)
      .then(applyCoApprovalSession)
      .catch(loadError => {
        console.error('Failed to load Co-Approval link', loadError);
        toast.error('Failed to load Co-Approval link');
      });
  }, [event?.id, user?.uid, coApprovalEnabled]);

  // VIP Trigger handlers
  const handleEnableVipTrigger = async () => {
    if (!event?.id || !user?.uid) return;
    setVipLoading(true);
    try {
      const { token } = await createVipSession(event.id, user.uid);
      setVipTriggerToken(token);
      setVipTriggerEnabled(true);
      const updated = await updateEvent(event.id, { settings: { ...buildCurrentSettings(), vipTriggerEnabled: true } });
      setEvent(updated);
      toast.success('VIP Trigger enabled!');
    } catch {
      toast.error('Failed to enable VIP Trigger');
    } finally {
      setVipLoading(false);
    }
  };

  const handleDisableVipTrigger = async () => {
    if (!event?.id || !user?.uid) return;
    setVipLoading(true);
    try {
      const session = await getVipSession(event.id, user.uid);
      if (session) await deleteVipSession(session.id);
      setVipTriggerEnabled(false);
      setVipTriggerToken('');
      const updated = await updateEvent(event.id, { settings: { ...buildCurrentSettings(), vipTriggerEnabled: false } });
      setEvent(updated);
      toast.success('VIP Trigger disabled');
    } catch {
      toast.error('Failed to disable VIP Trigger');
    } finally {
      setVipLoading(false);
    }
  };

  const handleRegenerateVipToken = async () => {
    if (!event?.id || !user?.uid) return;
    setVipLoading(true);
    try {
      const oldSession = await getVipSession(event.id, user.uid);
      if (oldSession) await deleteVipSession(oldSession.id);
      const { token } = await createVipSession(event.id, user.uid);
      setVipTriggerToken(token);
      const updated = await updateEvent(event.id, { settings: buildCurrentSettings() });
      setEvent(updated);
      toast.success('VIP link regenerated. Previous link is now invalid.');
    } catch {
      toast.error('Failed to regenerate VIP link');
    } finally {
      setVipLoading(false);
    }
  };

  const vipQrUrl = vipTriggerEnabled && vipTriggerToken && event?.id
    ? generateVipQrUrl(event.id, vipTriggerToken)
    : null;

  const vipShareUrl = vipTriggerEnabled && vipTriggerToken && event?.id
    ? getVipPageUrl(event.id, vipTriggerToken)
    : '';

  const copyVipLink = () => {
    if (vipShareUrl) {
      navigator.clipboard.writeText(vipShareUrl);
      toast.success('VIP link copied!');
    }
  };

  // Co-Approval handlers
  // The co-approver's email lives on the session doc and saves through its own button, so it is not an event setting
  function applyCoApprovalSession(session: CoApprovalSession | null) {
    setCoApprovalToken(session?.token ?? '');
    setCoApproverEmailSaved(session?.email ?? null);
    setCoApproverEmailDraft(session?.email ?? '');
    setCoApproverAlertsEnabled(session?.alertsEnabled !== false);
    setCoApproverLinkEmailStatus(session?.linkEmailStatus ?? null);
  }

  const saveCoApproverEmailAndReport = async (email: string | null) => {
    if (!event?.id) return;
    setCoApproverEmailSending(true);
    try {
      const saved = await saveCoApproverEmail(event.id, email);
      setCoApproverEmailSaved(saved.email);
      setCoApproverEmailDraft(saved.email ?? '');
      setCoApproverLinkEmailStatus(saved.linkEmailStatus);
      if (saved.email) setCoApproverAlertsEnabled(true);
    } catch (saveError) {
      console.error('Co-approver email save failed', saveError);
      if (saveError instanceof FirebaseError && saveError.code === 'functions/invalid-argument') {
        toast.error('Enter a valid email address');
      } else {
        toast.error("Couldn't save the co-approver's email. Try again.");
      }
    } finally {
      setCoApproverEmailSending(false);
    }
  };

  const coApproverEmailDraftClean = coApproverEmailDraft.trim().toLowerCase();
  const coApproverLinkAlreadySent = coApproverEmailDraftClean === coApproverEmailSaved && coApproverLinkEmailStatus === 'sent';

  const handleEnableCoApproval = async () => {
    if (!event?.id || !user?.uid) return;
    setCoApprovalLoading(true);
    try {
      const { token } = await createCoApprovalSession(event.id, user.uid);
      setCoApprovalToken(token);
      setCoApprovalEnabled(true);
      const updated = await updateEvent(event.id, { settings: { ...buildCurrentSettings(), coApprovalEnabled: true } });
      setEvent(updated);
      toast.success('Co-Approval enabled!');
    } catch {
      toast.error('Failed to enable Co-Approval');
    } finally {
      setCoApprovalLoading(false);
    }
  };

  const handleDisableCoApproval = async () => {
    if (!event?.id || !user?.uid) return;
    setCoApprovalLoading(true);
    try {
      const session = await getCoApprovalSession(event.id, user.uid);
      if (session) await deleteCoApprovalSession(session.id);
      setCoApprovalEnabled(false);
      applyCoApprovalSession(null);
      const updated = await updateEvent(event.id, { settings: { ...buildCurrentSettings(), coApprovalEnabled: false } });
      setEvent(updated);
      toast.success('Co-Approval disabled');
    } catch {
      toast.error('Failed to disable Co-Approval');
    } finally {
      setCoApprovalLoading(false);
    }
  };

  const handleRegenerateCoApprovalToken = async () => {
    if (!event?.id || !user?.uid) return;
    setCoApprovalLoading(true);
    try {
      const oldSession = await getCoApprovalSession(event.id, user.uid);
      if (oldSession) await deleteCoApprovalSession(oldSession.id);
      const { token } = await createCoApprovalSession(event.id, user.uid);
      setCoApprovalToken(token);
      const updated = await updateEvent(event.id, { settings: buildCurrentSettings() });
      setEvent(updated);
      toast.success('Co-Approval link regenerated. Previous link is now invalid.');
      // The new session starts with no email, so this call is what carries the address over and emails the new link
      const emailToCarryOver = coApproverEmailSaved;
      if (emailToCarryOver) {
        setCoApproverEmailSaved(null);
        setCoApproverLinkEmailStatus(null);
        await saveCoApproverEmailAndReport(emailToCarryOver);
      }
    } catch {
      toast.error('Failed to regenerate Co-Approval link');
    } finally {
      setCoApprovalLoading(false);
    }
  };

  const coApprovalQrUrl = coApprovalEnabled && coApprovalToken && event?.id
    ? generateCoApprovalQrUrl(event.id, coApprovalToken)
    : null;

  const coApprovalShareUrl = coApprovalEnabled && coApprovalToken && event?.id
    ? getCoApprovalPageUrl(event.id, coApprovalToken)
    : '';

  const copyCoApprovalLink = () => {
    if (coApprovalShareUrl) {
      navigator.clipboard.writeText(coApprovalShareUrl);
      toast.success('Co-Approval link copied!');
    }
  };

  // NFC tag writing
  const handleWriteNfc = async (url: string, type: 'event' | 'verification') => {
    setNfcWriting(true);
    setNfcWritingType(type);
    const controller = new AbortController();
    nfcAbortRef.current = controller;
    try {
      const ndef = new NDEFReader();
      await ndef.write(
        { records: [{ recordType: 'url', data: url }] },
        { signal: controller.signal }
      );
      if (nfcLockTag) {
        await ndef.makeReadOnly({ signal: controller.signal });
        toast.success(type === 'verification'
          ? 'Verification NFC tag written and permanently locked!'
          : 'NFC tag written and permanently locked!');
      } else {
        toast.success(type === 'verification'
          ? 'Verification NFC tag written! Attendees can tap to verify.'
          : 'NFC tag written! Attendees can tap to join.');
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        toast('NFC write cancelled', { icon: '✋' });
      } else {
        toast.error('NFC write failed. Make sure NFC is enabled.');
      }
    } finally {
      setNfcWriting(false);
      setNfcWritingType(null);
      nfcAbortRef.current = null;
    }
  };

  const handleCancelNfc = () => {
    nfcAbortRef.current?.abort();
    nfcAbortRef.current = null;
    setNfcWriting(false);
    setNfcWritingType(null);
  };

  // Toggle checklist item
  const toggleChecklistItem = (itemId: string) => {
    if (!event?.id) return;

    const updated = offlineChecklist.map(item =>
      item.id === itemId ? { ...item, checked: !item.checked } : item
    );
    setOfflineChecklist(updated);
    saveOfflineChecklist(event.id, updated);
  };

  // Calculate checklist progress
  const checklistProgress = offlineChecklist.filter(item => item.checked).length;
  const checklistTotal = offlineChecklist.length;

  const eventShareUrl = `${window.location.origin}/e/${event?.eventUrl || ''}`;

  const [saveLoading, setSaveLoading] = useState(false);
  const formStartDate = getEventStartDate({ date: eventDate, startDate: eventDate, startTime });
  const { closeAtMs: savedCloseAtMs } = useRequestWindow(event?.settings);

  const currentVoteRuleValues = (): VoteRuleValues => ({
    downvotingEnabled,
    voteOutEnabled,
    voteOutThresholdPercent,
    upvoteOverrideEnabled,
    upvoteOverridePercent,
  });

  const buildCurrentSettings = (
    playModeToSave: PlayMode = playMode,
    clampLimit: number | null = savedLimitFloor,
    voteRuleValues: VoteRuleValues = currentVoteRuleValues(),
  ) => ({
    ...omitLegacyTokens(event?.settings),
    requestsOpen: isActive,
    closeRequestsAtStart,
    requestsCloseAtMs: nextRequestsCloseAtMs({
      closeAtStart: closeRequestsAtStart,
      startMs: getEventStartDate({ date: eventDate, startDate: eventDate, startTime })?.getTime() ?? null,
      previous: {
        closeAtStart: event?.settings?.closeRequestsAtStart ?? false,
        startMs: getEventStartDate(event)?.getTime() ?? null,
        closeAtMs: requestsCloseAtMsOf(event?.settings),
      },
    }),
    autoApprove,
    pendingEmailAlerts,
    maxQueueSize,
    blockExplicit,
    filterExplicit: blockExplicit,
    warnExplicit,
    votingCloseTime: votingCloseTime || undefined,
    votingOpenTime: votingOpenTime || undefined,
    votingScheduledToOpen: votingScheduledToOpen && !!votingOpenTime,
    votingClosed: (votingScheduledToOpen && !!votingOpenTime && !isActive)
      ? true
      : isActive
        ? false
        : (event?.settings?.votingClosed ?? false),
    preEventQueueView,
    postCloseQueueView,
    duringEventQueueView,
    queueMode,
    rankSize: queueMode === 'ranked' ? rankSize : undefined,
    rankingStalenessMinutes: queueMode === 'ranked' ? rankingStalenessMinutes : undefined,
    rankingMinParticipants: queueMode === 'ranked' ? rankingMinParticipants : undefined,
    cooldownMinutes: requestLimitsEnabled ? cooldownMinutes : 0,
    maxRequestsPerUser: requestLimitsEnabled
      ? (clampLimit !== null ? Math.max(maxRequestsPerUser, clampLimit) : maxRequestsPerUser)
      : REQUEST_LIMITS_OFF_VALUE,
    // A legacy event with no stored mode keeps it absent, so the lock rule compares null with null
    playMode: event?.settings?.playMode === undefined && playModeToSave === LEGACY_PLAY_MODE ? undefined : playModeToSave,
    // FIFO turns downvoting off. A locked event that was already FIFO keeps its stored value, so its saves aren't refused
    downvotingEnabled: queueMode !== 'fifo'
      ? voteRuleValues.downvotingEnabled
      : (voteRulesLocked && event?.settings?.queueMode === 'fifo' ? (event?.settings?.downvotingEnabled ?? false) : false),
    voteOutEnabled: voteRuleValues.voteOutEnabled,
    voteOutThresholdPercent: voteRuleValues.voteOutThresholdPercent,
    upvoteOverrideEnabled: voteRuleValues.upvoteOverrideEnabled,
    upvoteOverridePercent: voteRuleValues.upvoteOverridePercent,
    allowOverlap,
    crossfadeSeconds,
    autoRejectBlocked,
    allowAttendeeFlagging,
    doomVotingEnabled,
    doomVotingMaxVotes: doomVotingEnabled ? doomVotingMaxVotes : undefined,
    doomVotingActiveMinutes: doomVotingEnabled ? doomVotingActiveMinutes : undefined,
    doomVotingRestMinutes: doomVotingEnabled ? doomVotingRestMinutes : undefined,
    superRequestEnabled,
    superRequestThresholdType: superRequestEnabled ? superRequestThresholdType : undefined,
    superRequestThresholdCount: superRequestEnabled ? superRequestThresholdCount : undefined,
    superRequestThresholdPercent: superRequestEnabled ? superRequestThresholdPercent : undefined,
    maxNominationsPerUser: superRequestEnabled ? maxNominationsPerUser : undefined,
    overflowEnabled,
    hideRankingInfo,
    lotteryEnabled,
    lotteryAllowRepeatWinners,
    triviaEnabled,
    localFilesEnabled,
    autoApplyMatchFromOtherEvents: autoApplyMatch,
  });

  const readFreshEvent = async (): Promise<DJEvent | null> => {
    if (!event) return null;
    try {
      return await getEventById(event.id);
    } catch (readErr) {
      console.error('Failed to re-read event after a refused save:', readErr);
      return null;
    }
  };

  const prefillLimitFromDefaults = async () => {
    if (maxRequestsPerUser <= MAX_REQUEST_LIMIT) return;
    let defaults = SYSTEM_DEFAULTS;
    if (user) {
      try {
        defaults = await getDJDefaults(user.uid);
      } catch (err) {
        console.error('Failed to load DJ defaults for request limits:', err);
      }
    }
    setMaxRequestsPerUser(startingRequestLimit(defaults.maxRequestsPerUser, activeLimitFloor));
  };

  const handleSubmit = async () => {
    if (!event) return;

    // Validate max queue size range
    if (maxQueueSize < 1 || maxQueueSize > 200) {
      toast.error('Max queue size must be between 1 and 200');
      return;
    }

    // Validate request limit settings
    if (requestLimitsEnabled) {
      if (cooldownMinutes < 0 || cooldownMinutes > 120) {
        toast.error('Cooldown must be between 0 and 120 minutes');
        return;
      }
      if (maxRequestsPerUser < 1 || maxRequestsPerUser > 50) {
        toast.error('Max requests per attendee must be between 1 and 50');
        return;
      }
      if (limitLocked && activeLimitFloor !== null && maxRequestsPerUser < activeLimitFloor) {
        toast.error(requestLimitRefusalText(activeLimitFloor));
        setCorrectedFromLimit(maxRequestsPerUser);
        setMaxRequestsPerUser(activeLimitFloor);
        setLimitFloorWarning(true);
        return;
      }
    }

    const contactInfo = (contactName || contactPhone || contactEmail)
      ? { name: contactName || undefined, phone: contactPhone || undefined, email: contactEmail || undefined }
      : undefined;
    const saveEvent = (playModeToSave: PlayMode, clampLimit: number | null, voteRuleValues?: VoteRuleValues) => updateEvent(event.id, {
      name,
      date: eventDate,
      startTime,
      startDate: eventDate,
      startAtMs: eventStartAtMs({ date: eventDate, startDate: eventDate, startTime }),
      endTime: endTime || undefined,
      endDate: (() => {
        if (endTime && startTime && endTime < startTime) {
          const d = new Date(eventDate + 'T00:00:00');
          d.setDate(d.getDate() + 1);
          return d.toISOString().slice(0, 10);
        }
        return eventDate;
      })(),
      location: location || undefined,
      address: address || undefined,
      description: description || undefined,
      contactInfo,
      settings: buildCurrentSettings(playModeToSave, clampLimit, voteRuleValues),
    });

    if (normalizeEventName(name) !== normalizeEventName(event.name)) {
      setSaveLoading(true);
      let existingEvents: DJEvent[];
      try {
        existingEvents = await getEventsByDjId(event.djId);
      } catch (checkError) {
        console.error('Could not load events for the duplicate-name check:', checkError);
        toast.error('Could not check your event names. Your changes were not saved. Try again.');
        setSaveLoading(false);
        return;
      }
      const duplicate = findDuplicateEventName(name, existingEvents, event.id);
      if (duplicate) {
        const duplicateMessage = duplicateEventNameMessage(duplicate);
        toast.error(duplicateMessage);
        setNameError(duplicateMessage);
        setSaveLoading(false);
        return;
      }
    }

    setSaveLoading(true);

    const currentSavedLimit = refusedLimitFloor ?? event.settings?.maxRequestsPerUser ?? 5;
    const desiredLimit = requestLimitsEnabled ? maxRequestsPerUser : REQUEST_LIMITS_OFF_VALUE;
    let clampLimit = savedLimitFloor;
    let loweredByServer = false;
    if (limitLocked && desiredLimit < currentSavedLimit) {
      try {
        await setRequestLimit(event.id, desiredLimit);
        clampLimit = desiredLimit;
        loweredByServer = true;
      } catch (err) {
        console.error('Request limit change refused:', err);
        const freshFloor = requestLimitRefusalFloor(err);
        if (freshFloor !== null) {
          setMaxRequestsPerUser(freshFloor);
          setLimitFloorWarning(true);
          toast.error(requestLimitRefusalText(freshFloor));
        } else {
          toast.error('Failed to change the request limit. Your other changes were not saved yet. Try again.');
        }
        setSaveLoading(false);
        return;
      }
    }

    try {
      const updatedEvent = await saveEvent(playMode, clampLimit);
      // Update parent context so navigating away and back reflects saved values
      if (setEvent) setEvent(updatedEvent);
      setRefusedLimitFloor(null);
      setLimitFloorWarning(false);
      setCorrectedFromLimit(null);
      if (requestLimitsEnabled && correctedFromLimit !== null) {
        toast(requestLimitCorrectedSaveText(Math.max(maxRequestsPerUser, clampLimit ?? 0), correctedFromLimit), { icon: '⚠️', duration: 8000 });
      } else {
        toast.success('Settings saved successfully!');
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Settings save error:', err);
      const freshEvent = (err as { code?: string })?.code === 'permission-denied'
        ? await readFreshEvent()
        : null;
      const freshMode = playModeOf(freshEvent);
      const modeRefused = freshEvent !== null && isPlayModeLocked(freshEvent) && freshMode.id !== playMode;
      if (modeRefused) {
        setPlayModeLockKnown(true);
        setPlayMode(freshMode.id);
        toast.error(`This event got its first request, so the player is locked to ${freshMode.playerName}.`);
      }
      const freshVoteValues = freshEvent ? voteRuleValuesOf(freshEvent.settings) : null;
      const voteRulesRefused = freshEvent !== null && freshVoteValues !== null && isVoteRulesLocked(freshEvent)
        && !voteRuleValuesEqual(freshVoteValues, currentVoteRuleValues());
      if (voteRulesRefused && freshVoteValues) {
        setVoteRulesLockKnown(true);
        setDownvotingEnabled(freshVoteValues.downvotingEnabled);
        setVoteOutEnabled(freshVoteValues.voteOutEnabled);
        setVoteOutThresholdPercent(freshVoteValues.voteOutThresholdPercent);
        setUpvoteOverrideEnabled(freshVoteValues.upvoteOverrideEnabled);
        setUpvoteOverridePercent(freshVoteValues.upvoteOverridePercent);
        toast.error('This event got its first request, so its crowd voting rules are locked.');
      }
      const freshSavedLimit = freshEvent ? requestLimitFloor(freshEvent) : null;
      const attemptedLimit = requestLimitsEnabled
        ? (clampLimit !== null ? Math.max(maxRequestsPerUser, clampLimit) : maxRequestsPerUser)
        : REQUEST_LIMITS_OFF_VALUE;
      const limitRefused = freshSavedLimit !== null && attemptedLimit < freshSavedLimit;
      if (loweredByServer && !limitRefused) {
        setRefusedLimitFloor(desiredLimit);
      }
      if ((modeRefused || voteRulesRefused) && !limitRefused) {
        // Only locked values were refused: save everything else with the locked player and crowd rules
        try {
          const updatedEvent = await saveEvent(
            modeRefused ? freshMode.id : playMode,
            clampLimit,
            voteRulesRefused && freshVoteValues ? freshVoteValues : undefined,
          );
          if (setEvent) setEvent(updatedEvent);
          setRefusedLimitFloor(null);
          setLimitFloorWarning(false);
          setCorrectedFromLimit(null);
          toast.success('Your other changes were saved.');
        } catch (retryErr) {
          console.error('Settings save retry with the locked values failed:', retryErr);
          toast.error('Failed to save your other changes. Try saving again.');
        }
      } else if (!limitRefused || freshSavedLimit === null) {
        toast.error('Failed to save settings');
      } else {
        console.error('Request limit save refused; the saved limit is now', freshSavedLimit);
        setRefusedLimitFloor(freshSavedLimit);
        if (freshSavedLimit > MAX_REQUEST_LIMIT) {
          setRequestLimitsEnabled(false);
        } else if (requestLimitsEnabled && maxRequestsPerUser < freshSavedLimit) {
          setMaxRequestsPerUser(freshSavedLimit);
        }
        toast.error(`The request limit was changed to ${freshSavedLimit} on another screen. Check it and save again.`);
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(eventShareUrl);
    toast.success('Link copied to clipboard!');
  };

  const handleDiscardChanges = () => {
    setName(initialValues.name);
    setNameError(null);
    setEventDate(initialValues.eventDate);
    setStartTime(initialValues.startTime);
    setEndTime(initialValues.endTime);
    setLocation(initialValues.location);
    setAddress(initialValues.address);
    setDescription(initialValues.description);
    setContactName(initialValues.contactName);
    setContactPhone(initialValues.contactPhone);
    setContactEmail(initialValues.contactEmail);
    setIsActive(initialValues.isActive);
    setCloseRequestsAtStart(initialValues.closeRequestsAtStart);
    setAllowTips(initialValues.allowTips);
    setMaxQueueSize(initialValues.maxQueueSize);
    setAutoApprove(initialValues.autoApprove);
    setPendingEmailAlerts(initialValues.pendingEmailAlerts);
    setQueueMode(initialValues.queueMode);
    setBlockExplicit(initialValues.blockExplicit);
    setWarnExplicit(initialValues.warnExplicit);
    setAutoRejectBlocked(initialValues.autoRejectBlocked);
    setAllowAttendeeFlagging(initialValues.allowAttendeeFlagging);
    setDoomVotingEnabled(initialValues.doomVotingEnabled);
    setDoomVotingMaxVotes(initialValues.doomVotingMaxVotes);
    setDoomVotingActiveMinutes(initialValues.doomVotingActiveMinutes);
    setDoomVotingRestMinutes(initialValues.doomVotingRestMinutes);
    setSuperRequestEnabled(initialValues.superRequestEnabled);
    setSuperRequestThresholdType(initialValues.superRequestThresholdType);
    setSuperRequestThresholdCount(initialValues.superRequestThresholdCount);
    setSuperRequestThresholdPercent(initialValues.superRequestThresholdPercent);
    setMaxNominationsPerUser(initialValues.maxNominationsPerUser);
    setCooldownMinutes(initialValues.cooldownMinutes);
    setMaxRequestsPerUser(initialValues.maxRequestsPerUser);
    setPlayMode(initialValues.playMode);
    setAllowOverlap(initialValues.allowOverlap);
    setCrossfadeSeconds(initialValues.crossfadeSeconds);
    setRankSize(initialValues.rankSize);
    setRankingStalenessMinutes(initialValues.rankingStalenessMinutes);
    setOverflowEnabled(initialValues.overflowEnabled);
    setHideRankingInfo(initialValues.hideRankingInfo);
    setLotteryEnabled(initialValues.lotteryEnabled);
    setLotteryAllowRepeatWinners(initialValues.lotteryAllowRepeatWinners);
    setTriviaEnabled(initialValues.triviaEnabled);
    setLocalFilesEnabled(initialValues.localFilesEnabled);
    setAutoApplyMatch(initialValues.autoApplyMatch);
    resetUncheckedFields();
    setLimitFloorWarning(false);
    setCorrectedFromLimit(null);
    toast('Changes discarded', { icon: '\u21A9\uFE0F' });
  };

  const handleReset = () => {
    setIsActive(DEFAULT_SETTINGS.isActive);
    setCloseRequestsAtStart(DEFAULT_SETTINGS.closeRequestsAtStart);
    setAllowTips(DEFAULT_SETTINGS.allowTips);
    setMaxQueueSize(DEFAULT_SETTINGS.maxQueueSize);
    setAutoApprove(DEFAULT_SETTINGS.autoApprove);
    setPendingEmailAlerts(DEFAULT_SETTINGS.pendingEmailAlerts);
    setQueueMode(DEFAULT_SETTINGS.queueMode);
    // Keep the event name unchanged
    toast.success('Settings reset to defaults');
  };

  const handleSaveAsDefault = async () => {
    if (!user) return;
    try {
      await saveDJDefaults(user.uid, {
        maxQueueSize,
        queueMode,
        isActive,
        allowTips,
        autoApprove,
        filterExplicit: blockExplicit,
        blockExplicit,
        warnExplicit,
        cooldownMinutes,
        maxRequestsPerUser,
        downvotingEnabled,
        voteOutEnabled,
        voteOutThresholdPercent,
        upvoteOverrideEnabled,
        upvoteOverridePercent,
        rankSize,
        rankingStalenessMinutes,
        rankingMinParticipants,
        playMode,
        preEventQueueView,
        postCloseQueueView,
        duringEventQueueView,
        autoRejectBlocked,
        allowAttendeeFlagging,
        doomVotingEnabled,
        doomVotingMaxVotes,
        doomVotingActiveMinutes,
        doomVotingRestMinutes,
        superRequestEnabled,
        superRequestThresholdType,
        superRequestThresholdCount,
        superRequestThresholdPercent,
        maxNominationsPerUser,
        hideRankingInfo,
        lotteryAllowRepeatWinners,
        autoMatchFromLibrary: autoApplyMatch,
      });
      toast.success('Current settings saved as your defaults for new events');
    } catch {
      toast.error('Failed to save defaults');
    }
  };

  const handleStatusChange = async (newStatus: 'upcoming' | 'active' | 'completed') => {
    if (!event || newStatus === eventStatus) return;

    // Ending and reopening ask first, the same as the Queue page's End Event button (ITER-199)
    const ending = newStatus === 'completed';
    const reopening = eventStatus === 'completed';
    if (ending && !window.confirm(END_EVENT_CONFIRM_TEXT)) return;
    if (reopening && !window.confirm(REOPEN_EVENT_CONFIRM_TEXT)) return;

    // Mark as manual override to prevent auto-revert
    manualStatusOverrideRef.current = true;

    setStatusLoading(true);
    try {
      if (ending) {
        await endEvent(event.id);
      } else if (reopening && newStatus === 'active') {
        await reopenEvent(event.id);
      } else {
        await updateEvent(event.id, { status: newStatus });
      }
      setEventStatus(newStatus);
      const statusLabels = { upcoming: 'Upcoming', active: 'Active', completed: 'Completed' };
      toast.success(`Event status changed to ${statusLabels[newStatus]}`);
    } catch (error) {
      console.error('Could not update the event status:', error);
      toast.error('Failed to update event status');
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDeleteEvent = async () => {
    if (!event) return;

    setDeleteLoading(true);

    try {
      // Simulate network delay
      await new Promise(resolve => setTimeout(resolve, 500));

      await deleteEvent(event.id);
      toast.success('Event deleted successfully');
      navigate('/dashboard');
    } catch {
      toast.error('Failed to delete event');
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!user?.uid || !templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    setTemplateSaveLoading(true);
    try {
      await createTemplate(user.uid, {
        name: templateName.trim(),
        settings: {
          blockExplicit: event?.settings?.filterExplicit ?? false,
          profanityFilter: true,
          requireApproval: !autoApprove,
          warnExplicit: true,
          votingEnabled: true,
          requestsOpen: isActive,
          maxRequestsPerUser: 10,
          postCloseRequestLimit: 0,
          autoApprove,
          cooldownMinutes: event?.settings?.cooldownMinutes ?? 5,
          votingClosed: false,
        },
      });
      toast.success(`Template "${templateName}" saved successfully!`);
      setShowSaveTemplateModal(false);
      setTemplateName('');
    } catch {
      toast.error('Failed to save template');
    } finally {
      setTemplateSaveLoading(false);
    }
  };

  // ---- Import from Past Event handlers ----
  const handleOpenImportModal = async () => {
    if (!user?.uid) return;
    setShowImportModal(true);
    setImportStep('pick-event');
    setImportResult(null);
    setSelectedPastEvent(null);
    setPastEventSongs([]);
    setSelectedSongIds(new Set());
    setPastEventsLoading(true);
    try {
      const events = await getEventsByDjId(user.uid);
      const otherEvents = events.filter(e => e.id !== event?.id);
      setPastEvents(otherEvents);
    } catch {
      toast.error('Failed to load past events');
      setShowImportModal(false);
    } finally {
      setPastEventsLoading(false);
    }
  };

  const handleSelectPastEvent = async (pastEvent: DJEvent) => {
    setSelectedPastEvent(pastEvent);
    setImportStep('pick-songs');
    setPastSongsLoading(true);
    try {
      const requests = await getRequestsByEventId(pastEvent.id);
      const importable = requests.filter(r => r.status === 'queued' || r.status === 'played' || r.status === 'nowPlaying');
      setPastEventSongs(importable);
      setSelectedSongIds(new Set(partitionForImport(importable, savedPlayMode).fits.map(r => r.id)));
    } catch {
      toast.error('Failed to load songs from event');
      setImportStep('pick-event');
    } finally {
      setPastSongsLoading(false);
    }
  };

  const toggleSongSelection = (songId: string) => {
    setSelectedSongIds(prev => {
      const next = new Set(prev);
      if (next.has(songId)) next.delete(songId);
      else next.add(songId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedSongIds.size === pastEventSongs.length) {
      setSelectedSongIds(new Set());
    } else {
      setSelectedSongIds(new Set(pastEventSongs.map(r => r.id)));
    }
  };

  const handleImportSongs = async () => {
    if (!user?.uid || !event?.id) return;
    const songsToImport = pastEventSongs.filter(r => selectedSongIds.has(r.id));
    if (songsToImport.length === 0) {
      toast.error('No songs selected');
      return;
    }
    setImporting(true);
    try {
      const result = await importSongsFromPastEvent(event.id, songsToImport, user.uid, savedPlayMode);
      setImportResult(result);
      setPlayModeLockKnown(true);
      const totalImported = result.importedToQueue + result.importedToPending;
      if (totalImported > 0) {
        const parts: string[] = [];
        if (result.importedToQueue > 0) parts.push(`${result.importedToQueue} to Queue`);
        if (result.importedToPending > 0) parts.push(`${result.importedToPending} to Pending`);
        if (result.skippedDuplicates > 0) parts.push(`${result.skippedDuplicates} duplicates skipped`);
        toast.success(`Imported: ${parts.join(', ')}`);
        // Save the imported songs' links to the shared library (fire-and-forget; reported in a follow-up toast)
        const linkEntries: SaveSongLinkEntry[] = [];
        let unparsedLinks = 0;
        for (const s of songsToImport) {
          if (!s.platformLink && !s.spotifyUri) continue;
          const link = s.platformLink || `https://open.spotify.com/track/${s.spotifyUri?.split(':').pop() || ''}`;
          if (parseMusicLink(link).kind !== 'track') {
            unparsedLinks++;
            continue;
          }
          linkEntries.push({
            song: librarySongOf(s.song),
            link,
            songLinkUrl: s.songLinkUrl || null,
            albumArtUrl: s.song.albumArtUrl || null,
            durationMs: s.song.durationMs || null,
            durationVerified: s.durationVerified || null,
            eventId: s.eventId,
          });
        }
        if (linkEntries.length > 0) {
          saveSongLinks(linkEntries)
            .then(results => {
              const accepted = results.filter(r => r.ok);
              const queued = accepted.filter(r => r.ok && r.status === 'queued_for_review').length;
              const saved = accepted.length - queued;
              const skipped = results.length - accepted.length + unparsedLinks;
              const notes = [
                ...(queued > 0 ? [`${queued} sent to the owner because your library edits are paused`] : []),
                ...(skipped > 0 ? [`${skipped} skipped`] : []),
              ];
              toast.success(`Saved ${saved} link${saved !== 1 ? 's' : ''} to your library${notes.length > 0 ? ` (${notes.join(', ')})` : ''}`);
            })
            .catch(err => {
              console.error('[EventSettingsPage] Saving imported links to the library failed:', err);
              toast.error('Songs imported, but saving their links to your library failed');
            });
        }
        if (result.unmatchedRequestIds.length > 0) {
          toast(`Imported ${totalImported}. Match ${result.unmatchedRequestIds.length} song${result.unmatchedRequestIds.length !== 1 ? 's' : ''} for ${savedPlayMode.playerName} now.`);
          handleCloseImportModal();
          navigate(routerLocation.pathname, { state: { matchRunRequestIds: result.unmatchedRequestIds } });
        }
      } else {
        toast.error('All selected songs were duplicates — nothing imported');
      }
    } catch {
      toast.error('Failed to import songs');
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportStep('pick-event');
    setSelectedPastEvent(null);
    setPastEventSongs([]);
    setSelectedSongIds(new Set());
    setImportResult(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Event Settings</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Configure your event preferences
        </p>
      </div>

      {/* Disabled overlay when locked */}
      <fieldset disabled={isLocked} className={isLocked ? 'opacity-60 pointer-events-none' : undefined}>

      {/* Event Info (F10, F11, F12) */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
        <h3 className="font-medium text-gray-900 dark:text-white">Event Info</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="eventName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Event Name
            </label>
            <input id="eventName" type="text" value={name} onChange={(e) => { setName(e.target.value); setNameError(null); }} className="input" />
            {nameError && <p role="alert" className="text-xs text-red-600 dark:text-red-400 mt-1">{nameError}</p>}
          </div>
          <div>
            <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date
            </label>
            <input id="eventDate" type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} className="input" />
          </div>
          <div>
            <label htmlFor="startTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Start Time
            </label>
            <input id="startTime" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className="input" />
          </div>
          <div>
            <label htmlFor="endTime" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              End Time (optional)
            </label>
            <div className="flex items-center gap-2">
              <input id="endTime" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className="input flex-1" />
              {endTime && startTime && endTime < startTime && (
                <span className="text-xs text-amber-600 dark:text-amber-400 font-medium whitespace-nowrap">+1 day</span>
              )}
            </div>
            {endTime && (
              <label className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allowOverlap}
                  onChange={(e) => setAllowOverlap(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Allow last song to extend past end time
              </label>
            )}
          </div>
          <div>
            <label htmlFor="crossfadeSeconds" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Crossfade (seconds)
            </label>
            <input
              id="crossfadeSeconds"
              type="number"
              min={0}
              max={12}
              value={crossfadeSeconds}
              onChange={(e) => setCrossfadeSeconds(Math.min(12, Math.max(0, Number(e.target.value))))}
              className="input w-24"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Seconds of overlap between songs. Also sets the fade in and out of local files. 0 = straight cut.</p>
          </div>
          <div>
            <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Location (optional)
            </label>
            <input id="location" type="text" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. Community Center" className="input" />
          </div>
          <div>
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              <MapPin className="w-3.5 h-3.5 inline mr-1" />Address (optional)
            </label>
            <input id="address" type="text" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="123 Main St, City, State" className="input" />
          </div>
        </div>
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description (optional)
          </label>
          <textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} placeholder="Brief event description..." className="input" />
        </div>

        {/* Contact Info (F12) */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Contact Info (optional — shown to attendees)</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label htmlFor="contactName" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                <User className="w-3 h-3 inline mr-1" />Name
              </label>
              <input id="contactName" type="text" value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="DJ Name" className="input" />
            </div>
            <div>
              <label htmlFor="contactPhone" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                <Phone className="w-3 h-3 inline mr-1" />Phone
              </label>
              <input id="contactPhone" type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="(555) 123-4567" className="input" />
            </div>
            <div>
              <label htmlFor="contactEmail" className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                <Mail className="w-3 h-3 inline mr-1" />Email
              </label>
              <input id="contactEmail" type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="dj@example.com" className="input" />
            </div>
          </div>
        </div>
      </div>

      {/* Event Link & QR Code */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <div className="grid md:grid-cols-2 gap-6">
          {/* Share Link */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Share Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={eventShareUrl}
                readOnly
                className="input flex-1 bg-white dark:bg-gray-900"
              />
              <button onClick={copyLink} className="btn btn-secondary" title="Copy link">
                <Copy className="w-5 h-5" />
              </button>
              <a
                href={eventShareUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                title="Open event page"
              >
                <ExternalLink className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* QR Code */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              QR Code
            </label>
            <div className="flex items-center gap-4">
              {qrCodeUrl ? (
                <>
                  <button
                    onClick={() => setShowQrModal(true)}
                    className="w-20 h-20 bg-white rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden hover:shadow-lg transition-shadow"
                    title="Click to enlarge"
                  >
                    <img
                      src={qrCodeUrl}
                      alt="Event QR Code"
                      className="w-full h-full object-cover"
                    />
                  </button>
                  <div className="flex flex-col gap-2">
                    <button
                      onClick={handleDownloadQrCode}
                      className="btn btn-secondary text-sm"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </button>
                    <button
                      onClick={() => setShowQrPrint(true)}
                      className="btn btn-secondary text-sm"
                    >
                      <Printer className="w-4 h-4 mr-1" />
                      Print
                    </button>
                    <button
                      onClick={handleGenerateQrCode}
                      disabled={qrCodeLoading}
                      className="btn btn-secondary text-sm"
                    >
                      {qrCodeLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <QrCode className="w-4 h-4 mr-1" />
                          Regenerate
                        </>
                      )}
                    </button>
                    {nfcSupported && (
                      nfcWriting && nfcWritingType === 'event' ? (
                        <div className="flex gap-1">
                          <span className="btn bg-blue-500 text-white text-sm animate-pulse cursor-default flex-1">
                            <Smartphone className="w-4 h-4 mr-1" />
                            Hold tag...
                          </span>
                          <button onClick={handleCancelNfc} className="btn btn-secondary text-sm">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleWriteNfc(eventShareUrl, 'event')}
                          disabled={nfcWriting}
                          className="btn bg-blue-500 hover:bg-blue-600 text-white text-sm"
                        >
                          <Smartphone className="w-4 h-4 mr-1" />
                          NFC Tag
                        </button>
                      )
                    )}
                  </div>
                </>
              ) : (
                <button
                  onClick={handleGenerateQrCode}
                  disabled={qrCodeLoading}
                  className="btn btn-primary"
                >
                  {qrCodeLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <QrCode className="w-4 h-4 mr-2" />
                      Generate QR Code
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Event Status Control */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Event Status
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => handleStatusChange('upcoming')}
            disabled={statusLoading}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              eventStatus === 'upcoming'
                ? 'bg-blue-100 dark:bg-blue-900/30 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300'
                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Clock className="w-4 h-4" />
            Upcoming
          </button>
          <button
            type="button"
            onClick={() => handleStatusChange('active')}
            disabled={statusLoading}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              eventStatus === 'active'
                ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700 text-green-700 dark:text-green-300'
                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Play className="w-4 h-4" />
            Active
          </button>
          <button
            type="button"
            onClick={() => handleStatusChange('completed')}
            disabled={statusLoading}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
              eventStatus === 'completed'
                ? 'bg-gray-200 dark:bg-gray-700 border-gray-400 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <CheckCircle2 className="w-4 h-4" />
            Completed
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          {eventStatus === 'upcoming' && 'Event is scheduled but not yet started'}
          {eventStatus === 'active' && 'Event is currently live and accepting requests'}
          {eventStatus === 'completed' && 'Event has ended - no new requests allowed'}
        </p>
      </div>

      {/* On-Site Verification */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 dark:bg-teal-900/30 rounded-lg">
              <Shield className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">On-Site Verification</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Require attendees at the venue to scan a QR code to participate
              </p>
            </div>
          </div>
          <button
            type="button"
            disabled={verificationLoading}
            onClick={() => {
              if (onSiteVerificationEnabled) {
                handleDisableVerification();
              } else {
                setShowEnableVerificationModal(true);
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              onSiteVerificationEnabled ? 'bg-teal-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                onSiteVerificationEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {onSiteVerificationEnabled && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
            {/* Verified count */}
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-teal-500" />
              <span className="font-medium text-gray-900 dark:text-white">{verifiedCount}</span>
              <span className="text-gray-600 dark:text-gray-400">attendee{verifiedCount !== 1 ? 's' : ''} verified</span>
            </div>

            {/* Verification QR Code */}
            {verificationQrUrl && (
              <div className="flex items-start gap-4">
                <button
                  onClick={() => setShowVerificationQrModal(true)}
                  className="w-24 h-24 bg-white rounded-lg border-2 border-teal-200 dark:border-teal-700 overflow-hidden hover:shadow-lg transition-shadow flex-shrink-0"
                  title="Click to enlarge"
                >
                  <img
                    src={verificationQrUrl}
                    alt="Verification QR Code"
                    className="w-full h-full object-cover"
                  />
                </button>
                <div className="flex-1 min-w-0 space-y-2">
                  <p className="text-xs font-medium text-teal-700 dark:text-teal-400 uppercase tracking-wide">
                    Display this QR code at the venue
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={handleDownloadVerificationQr}
                      className="btn btn-secondary text-sm"
                    >
                      <Download className="w-4 h-4 mr-1" />
                      Download
                    </button>
                    <button
                      onClick={copyVerificationLink}
                      className="btn btn-secondary text-sm"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </button>
                    <button
                      onClick={() => setShowRegenerateTokenModal(true)}
                      disabled={verificationLoading}
                      className="btn btn-secondary text-sm"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Regenerate
                    </button>
                    {nfcSupported && (
                      nfcWriting && nfcWritingType === 'verification' ? (
                        <div className="flex gap-1">
                          <span className="btn bg-blue-500 text-white text-sm animate-pulse cursor-default">
                            <Smartphone className="w-4 h-4 mr-1" />
                            Hold tag...
                          </span>
                          <button onClick={handleCancelNfc} className="btn btn-secondary text-sm">
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleWriteNfc(verificationShareUrl, 'verification')}
                          disabled={nfcWriting}
                          className="btn bg-blue-500 hover:bg-blue-600 text-white text-sm"
                        >
                          <Smartphone className="w-4 h-4 mr-1" />
                          NFC Tag
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400">
              Unverified attendees can still view the queue but cannot request songs, vote, or rank.
            </p>
          </div>
        )}
      </div>

      {/* VIP Trigger */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <Crown className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">VIP Trigger</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Let a VIP trigger curated songs to play next</p>
            </div>
          </div>
          <button
            type="button"
            disabled={vipLoading}
            onClick={() => {
              if (vipTriggerEnabled) {
                handleDisableVipTrigger();
              } else {
                handleEnableVipTrigger();
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              vipTriggerEnabled ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                vipTriggerEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {vipTriggerEnabled && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
            {/* VIP QR Code */}
            {vipQrUrl && (
              <div className="flex items-start gap-4">
                <button
                  onClick={() => setShowVipQrModal(true)}
                  className="w-24 h-24 bg-white rounded-lg border-2 border-amber-200 dark:border-amber-700 overflow-hidden hover:shadow-lg transition-shadow flex-shrink-0"
                  title="Click to enlarge"
                >
                  <img
                    src={vipQrUrl}
                    alt="VIP Trigger QR Code"
                    className="w-full h-full object-cover"
                  />
                </button>
                <div className="flex-1 min-w-0 space-y-2">
                  <p className="text-xs font-medium text-amber-700 dark:text-amber-400 uppercase tracking-wide">
                    Share this link with your VIP
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={copyVipLink}
                      className="btn btn-secondary text-sm"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </button>
                    <button
                      onClick={handleRegenerateVipToken}
                      disabled={vipLoading}
                      className="btn btn-secondary text-sm"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Regenerate
                    </button>
                  </div>
                </div>
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400">
              Mark songs as "VIP triggerable" in the queue. The VIP can trigger them to play next at any time.
            </p>
          </div>
        )}
      </div>

      {/* VIP QR Modal */}
      {showVipQrModal && vipQrUrl && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowVipQrModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">VIP Trigger QR</h3>
              <button onClick={() => setShowVipQrModal(false)} className="btn btn-ghost btn-sm btn-circle">
                <X className="w-4 h-4" />
              </button>
            </div>
            <img src={vipQrUrl} alt="VIP QR Code" className="w-full" />
          </div>
        </div>
      )}

      {/* Co-Approval */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <ShieldCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Co-Approval</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Require third-party approval before songs enter queue</p>
            </div>
          </div>
          <button
            type="button"
            disabled={coApprovalLoading}
            onClick={() => {
              if (coApprovalEnabled) {
                handleDisableCoApproval();
              } else {
                handleEnableCoApproval();
              }
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              coApprovalEnabled ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                coApprovalEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {coApprovalEnabled && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
            {/* Co-Approval QR Code */}
            {coApprovalQrUrl && (
              <div className="flex items-start gap-4">
                <button
                  onClick={() => setShowCoApprovalQrModal(true)}
                  className="w-24 h-24 bg-white rounded-lg border-2 border-blue-200 dark:border-blue-700 overflow-hidden hover:shadow-lg transition-shadow flex-shrink-0"
                  title="Click to enlarge"
                >
                  <img
                    src={coApprovalQrUrl}
                    alt="Co-Approval QR Code"
                    className="w-full h-full object-cover"
                  />
                </button>
                <div className="flex-1 min-w-0 space-y-2">
                  <p className="text-xs font-medium text-blue-700 dark:text-blue-400 uppercase tracking-wide">
                    Share this link, or email it below
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={copyCoApprovalLink}
                      className="btn btn-secondary text-sm"
                    >
                      <Copy className="w-4 h-4 mr-1" />
                      Copy Link
                    </button>
                    <button
                      onClick={handleRegenerateCoApprovalToken}
                      disabled={coApprovalLoading}
                      className="btn btn-secondary text-sm"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Regenerate
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Co-Approver Email */}
            {coApprovalToken && (
              <div className="space-y-2">
                <label htmlFor="coApproverEmail" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Co-approver's email
                </label>
                <div className="flex flex-wrap gap-2">
                  <input
                    id="coApproverEmail"
                    type="email"
                    value={coApproverEmailDraft}
                    onChange={(e) => setCoApproverEmailDraft(e.target.value)}
                    placeholder="name@example.com"
                    className="input flex-1 min-w-[12rem]"
                  />
                  <button
                    onClick={() => saveCoApproverEmailAndReport(coApproverEmailDraftClean)}
                    disabled={coApproverEmailSending || !coApproverEmailDraftClean || coApproverLinkAlreadySent}
                    className="btn btn-secondary text-sm"
                  >
                    {coApproverEmailSending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                    Send link
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  They get the link by email, then one email each time songs start waiting for them.
                </p>
                {coApproverEmailSaved && coApproverLinkEmailStatus === 'sent' && (
                  <p className="text-xs text-green-700 dark:text-green-400">Link emailed to {coApproverEmailSaved}.</p>
                )}
                {coApproverEmailSaved && coApproverLinkEmailStatus === 'skipped' && (
                  <p className="text-xs text-amber-700 dark:text-amber-400">No email was sent: email isn't set up in this environment.</p>
                )}
                {coApproverEmailSaved && coApproverLinkEmailStatus === 'failed' && (
                  <p role="alert" className="text-xs text-red-600 dark:text-red-400">The email couldn't be sent. Copy the link and send it yourself.</p>
                )}
                {coApproverEmailSaved && (
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    {coApproverAlertsEnabled ? 'Alert emails: On' : 'Alert emails: Off (switched off by the co-approver)'}
                    <button
                      onClick={() => saveCoApproverEmailAndReport(null)}
                      disabled={coApproverEmailSending}
                      className="ml-3 text-red-600 dark:text-red-400 hover:underline disabled:opacity-50"
                    >
                      Remove email
                    </button>
                  </p>
                )}
              </div>
            )}

            <p className="text-xs text-gray-500 dark:text-gray-400">
              You review every song first (or your co-approval DJ does), then the co-approver gives final approval. Auto-approve is paused while co-approval is on.
            </p>
          </div>
        )}
      </div>

      {/* Co-Approval QR Modal */}
      {showCoApprovalQrModal && coApprovalQrUrl && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCoApprovalQrModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">Co-Approval QR</h3>
              <button onClick={() => setShowCoApprovalQrModal(false)} className="btn btn-ghost btn-sm btn-circle">
                <X className="w-4 h-4" />
              </button>
            </div>
            <img src={coApprovalQrUrl} alt="Co-Approval QR Code" className="w-full" />
          </div>
        </div>
      )}

      {/* Co-Approval DJ (ITER-194) */}
      {event?.id && user?.uid && <CoApprovalDjSettings eventId={event.id} djId={user.uid} />}

      {/* Local Files */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
              <FileMusic className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Local Files</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Upload and play local audio files through the queue</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              const newVal = !localFilesEnabled;
              setLocalFilesEnabled(newVal);
              toast.success(newVal ? 'Local Files enabled — save to apply' : 'Local Files disabled — save to apply');
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              localFilesEnabled ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                localFilesEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {localFilesEnabled && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Use the "Local File" button in the queue to upload audio files. With VoteBeats Bridge running, your player pauses and resumes around local files. Without Bridge, the Queue page tells you when to stop and start your player. Keep the Queue page open on the device that plays them.
            </p>
          </div>
        )}
      </div>

      {/* Cross-Event Match Propagation */}
      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
              <RefreshCw className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Auto-Match from Library</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Automatically queue songs that match your saved song library</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              const newVal = !autoApplyMatch;
              setAutoApplyMatch(newVal);
              toast.success(newVal ? 'Auto-match from library enabled — save to apply' : 'Auto-match from library disabled — save to apply');
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              autoApplyMatch ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                autoApplyMatch ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {autoApplyMatch && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              When an attendee requests a song you've previously matched, it will be automatically queued with the saved platform link. Works across all your events.
            </p>
          </div>
        )}
      </div>

      <div className="space-y-6">
        {/* Basic Settings */}
        <div className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Event Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameError(null); }}
              className="input"
              required
            />
            {nameError && <p role="alert" className="text-xs text-red-600 dark:text-red-400 mt-1">{nameError}</p>}
          </div>

          <div>
            <label htmlFor="maxQueue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Max Queue Size
            </label>
            <input
              id="maxQueue"
              type="number"
              min="1"
              max="200"
              value={maxQueueSize || ''}
              onChange={(e) => setMaxQueueSize(e.target.value === '' ? 0 : Number(e.target.value))}
              className="input w-32"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Range: 1–200 songs</p>
          </div>

          {/* Request Limits */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Request Limits</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Limit how often and how many songs attendees can request
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!requestLimitsEnabled) void prefillLimitFromDefaults();
                  setRequestLimitsEnabled(!requestLimitsEnabled);
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  requestLimitsEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    requestLimitsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {requestLimitsEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                <div>
                  <label htmlFor="maxRequestsPerUser" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Requests Per Attendee
                  </label>
                  <input
                    id="maxRequestsPerUser"
                    type="number"
                    min={activeLimitFloor ?? 1}
                    max="50"
                    value={maxRequestsPerUser || ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? 0 : Number(e.target.value);
                      setMaxRequestsPerUser(value);
                      setCorrectedFromLimit(null);
                      if (value >= (activeLimitFloor ?? 1)) setLimitFloorWarning(false);
                    }}
                    onBlur={() => {
                      if (activeLimitFloor !== null && maxRequestsPerUser < activeLimitFloor) {
                        setCorrectedFromLimit(maxRequestsPerUser);
                        setMaxRequestsPerUser(activeLimitFloor);
                        setLimitFloorWarning(true);
                      }
                    }}
                    className="input w-32"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {!limitLocked
                      ? 'Total songs each person can request (1–50)'
                      : highestActiveCount === null
                        ? 'Checking current requests…'
                        : requestLimitFloorText(highestActiveCount)}
                  </p>
                  {activeLimitFloor !== null && maxRequestsPerUser < activeLimitFloor && (
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">
                      Requests have started, so this limit can't go below {activeLimitFloor}.
                    </p>
                  )}
                  {activeLimitFloor !== null && maxRequestsPerUser >= activeLimitFloor && limitFloorWarning && (
                    <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{`It's set back to ${activeLimitFloor}, the lowest you can set right now.`}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="cooldownMinutes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cooldown Between Requests
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      id="cooldownMinutes"
                      type="number"
                      min="0"
                      max="120"
                      value={cooldownMinutes || ''}
                      onChange={(e) => setCooldownMinutes(e.target.value === '' ? 0 : Number(e.target.value))}
                      className="input w-32"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400">minutes</span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Wait time between requests, 0–120 min (0 = no wait)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Overflow Pool */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Overflow Pool</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  When enabled, attendees who hit their request limit can keep adding songs to a private waitlist. Raise the limit later to release their top choices into the queue.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOverflowEnabled(!overflowEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  overflowEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    overflowEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="queueMode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Queue Mode
            </label>
            <div className="relative">
              <ListOrdered className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <select
                id="queueMode"
                value={queueMode}
                onChange={(e) => setQueueMode(e.target.value as 'standard' | 'ranked' | 'fifo')}
                className="input pl-10 appearance-none cursor-pointer"
              >
                {QUEUE_MODES.map((mode) => (
                  <option key={mode.value} value={mode.value} disabled={mode.value === 'fifo' && fifoBlockedByVoteLock}>
                    {mode.value === 'fifo' && fifoBlockedByVoteLock ? `${mode.label} (locked: downvoting is on)` : mode.label}
                  </option>
                ))}
              </select>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {QUEUE_MODES.find(m => m.value === queueMode)?.description}
            </p>
          </div>

          {fifoBlockedByVoteLock && (
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
              First In, First Out turns downvoting off, and this event&apos;s crowd voting rules are locked since its first request.
            </p>
          )}

          {/* Downvoting toggle and crowd vote-out (only when not FIFO) */}
          {queueMode !== 'fifo' && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">Enable Downvoting</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Let attendees downvote songs. Downvotes count against a song&apos;s place in the queue.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setDownvotingEnabled(!downvotingEnabled)}
                  disabled={voteRulesLocked}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    downvotingEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      downvotingEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {downvotingEnabled && (
                <div className="ml-4 pl-3 border-l-2 border-gray-200 dark:border-gray-700 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">A majority of downvotes votes a song out</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        A song leaves the queue when more than {voteOutThresholdPercent}% of everyone taking part has downvoted it, and comes back when fewer than {voteOutThresholdPercent - RETURN_GAP_POINTS}% are against it. Starts once {MIN_PARTICIPANTS} people are taking part. You can&apos;t override the crowd while this is on.
                      </p>
                    </div>
                    <button
                  type="button"
                  onClick={() => setVoteOutEnabled(!voteOutEnabled)}
                  disabled={voteRulesLocked}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    voteOutEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      voteOutEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                  </div>
                  {voteOutEnabled && (
                    <div>
                      <label htmlFor="voteOutThresholdPercent" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Vote out above
                      </label>
                      <select
                        id="voteOutThresholdPercent"
                        value={voteOutThresholdPercent}
                        onChange={(e) => setVoteOutThresholdPercent(Number(e.target.value))}
                        disabled={voteRulesLocked}
                        className="input w-full disabled:opacity-50"
                      >
                        {VOTE_OUT_THRESHOLD_OPTIONS.map(option => <option key={option} value={option}>{option}%</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Crowd favorites (ITER-075, only when not FIFO) */}
          {queueMode !== 'fifo' && (
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white text-sm">Crowd favorites</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Jump a crowd favorite to the top. Needs {MIN_PARTICIPANTS} people taking part. Favorites play right after locked songs, ahead of rankings and your manual order.
                  </p>
                </div>
                <button
                  type="button"
                  aria-label="Jump a crowd favorite to the top"
                  onClick={() => setUpvoteOverrideEnabled(!upvoteOverrideEnabled)}
                  disabled={voteRulesLocked}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    upvoteOverrideEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      upvoteOverrideEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {upvoteOverrideEnabled && (
                <div>
                  <label htmlFor="upvoteOverridePercent" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    When at least … of everyone taking part upvotes it
                  </label>
                  <select
                    id="upvoteOverridePercent"
                    value={upvoteOverridePercent}
                    onChange={(e) => setUpvoteOverridePercent(Number(e.target.value))}
                    disabled={voteRulesLocked}
                    className="input w-full disabled:opacity-50"
                  >
                    {UPVOTE_OVERRIDE_OPTIONS.map(option => <option key={option} value={option}>{option}%</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {voteRulesLocked && queueMode !== 'fifo' && (
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Locked: this event has its first request. Crowd voting rules can&apos;t change now.
            </p>
          )}

          {/* Ranking Parameters (only shown for ranked mode) */}
          {queueMode === 'ranked' && (
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 space-y-4">
              <div className="flex items-center gap-2 mb-1">
                <ListOrdered className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <p className="font-medium text-gray-900 dark:text-white text-sm">Ranking Settings</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="rankSize" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Rank Size
                  </label>
                  <input
                    type="number"
                    id="rankSize"
                    value={rankSize}
                    onChange={(e) => setRankSize(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                    min={1}
                    max={20}
                    className="input w-full text-sm"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Each attendee ranks exactly {rankSize} song{rankSize !== 1 ? 's' : ''}
                  </p>
                </div>
                <div>
                  <label htmlFor="rankingStalenessMinutes" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Staleness Timeout (min)
                  </label>
                  <input
                    type="number"
                    id="rankingStalenessMinutes"
                    value={rankingStalenessMinutes}
                    onChange={(e) => setRankingStalenessMinutes(Math.max(0, Math.min(60, parseInt(e.target.value) || 0)))}
                    min={0}
                    max={60}
                    className="input w-full text-sm"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {rankingStalenessMinutes === 0 ? 'Disabled — all rankings count' : `Ignore an attendee's whole ranking while they haven't seen a song queued over ${rankingStalenessMinutes}m ago`}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    Off is recommended. Turn on if attendees who left are skewing the order.
                  </p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-800">
                <label htmlFor="rankingMinParticipants" className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Minimum Participants for Scores
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    id="rankingMinParticipants"
                    value={rankingMinParticipants}
                    onChange={(e) => setRankingMinParticipants(Math.max(1, parseInt(e.target.value) || 1))}
                    min={1}
                    max={100}
                    className="input w-32 text-sm"
                  />
                  <Users className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Consensus and discovery scores unlock after {rankingMinParticipants} participant{rankingMinParticipants !== 1 ? 's' : ''} submit rankings
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Toggle Settings */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Event Active</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Allow attendees to submit requests and vote
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                // If currently active and trying to deactivate, show confirmation
                if (isActive) {
                  setShowCloseVotingModal(true);
                } else {
                  setIsActive(true);
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isActive ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isActive ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Close requests at show start (ITER-137) */}
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="font-medium text-gray-900 dark:text-white">Close requests when the show starts</p>
              {formStartDate ? (
                <>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    At {formStartDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}, new song requests {formStartDate.getTime() <= Date.now() ? 'stopped' : 'stop'}. Guests can still vote and rank. You can reopen requests from the Queue page.
                  </p>
                  {closeRequestsAtStart && !(event?.settings?.closeRequestsAtStart ?? false) && formStartDate.getTime() <= Date.now() && (
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                      The show has started, so this closes requests as soon as you save.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-600 dark:text-gray-400">Set a start time to use this.</p>
              )}
              {savedCloseAtMs !== null && isClosedByShowStart(event?.settings, Date.now()) && (
                <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                  Requests closed automatically at {new Date(savedCloseAtMs).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}. Reopen them from the Queue page.
                </p>
              )}
            </div>
            <button
              type="button"
              disabled={!formStartDate && !closeRequestsAtStart}
              onClick={() => setCloseRequestsAtStart(!closeRequestsAtStart)}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                closeRequestsAtStart ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  closeRequestsAtStart ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Scheduled Voting Open Time */}
          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <p className="font-medium text-gray-900 dark:text-white">Schedule Voting Open</p>
                  <button
                    type="button"
                    onClick={() => {
                      setVotingScheduledToOpen(!votingScheduledToOpen);
                      if (!votingScheduledToOpen) {
                        // When enabling scheduled open, close voting now
                        setIsActive(false);
                      }
                    }}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      votingScheduledToOpen ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                        votingScheduledToOpen ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Set a specific date and time when voting will automatically open
                </p>
                {votingScheduledToOpen && (
                  <>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input
                        type="datetime-local"
                        value={votingOpenTime ? votingOpenTime.slice(0, 16) : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value) {
                            // Store as local ISO string (what the user typed)
                            setVotingOpenTime(value + ':00');
                          } else {
                            setVotingOpenTime('');
                          }
                        }}
                        className="input flex-1"
                      />
                      {votingOpenTime && (
                        <button
                          type="button"
                          onClick={() => setVotingOpenTime('')}
                          className="btn btn-secondary text-sm"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    {votingOpenTime && (
                      <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                        Voting will open: {new Date(votingOpenTime.replace('Z', '')).toLocaleString()}
                      </p>
                    )}
                    {!votingOpenTime && (
                      <p className="text-xs text-blue-700 dark:text-blue-400 mt-2">
                        ⚠️ Select a date and time for voting to open
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Scheduled Voting Close Time */}
          <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-gray-900 dark:text-white">Schedule Voting Close</p>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Set a specific date and time when voting will automatically close
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <input
                    type="datetime-local"
                    value={votingCloseTime ? votingCloseTime.slice(0, 16) : ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value) {
                        // Store as local ISO string (what the user typed)
                        setVotingCloseTime(value + ':00');
                      } else {
                        setVotingCloseTime('');
                      }
                    }}
                    className="input flex-1"
                  />
                  {votingCloseTime && (
                    <button
                      type="button"
                      onClick={() => setVotingCloseTime('')}
                      className="btn btn-secondary text-sm"
                    >
                      Clear
                    </button>
                  )}
                </div>
                {votingCloseTime && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-2">
                    Voting will close: {new Date(votingCloseTime.replace('Z', '')).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Allow Tips</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enable tipping on song requests
              </p>
            </div>
            <button
              type="button"
              onClick={() => setAllowTips(!allowTips)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                allowTips ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  allowTips ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Auto-Approve Requests</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Automatically add requests to queue
              </p>
              {autoApprove && coApprovalEnabled && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">Paused while co-approval is on</p>
              )}
            </div>
            <button
              type="button"
              onClick={() => setAutoApprove(!autoApprove)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                autoApprove ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  autoApprove ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Email me about pending requests</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                One email when requests are waiting before the event, then one reminder a day until you clear them.
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={pendingEmailAlerts}
              aria-label="Email me about pending requests"
              onClick={() => setPendingEmailAlerts(!pendingEmailAlerts)}
              className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                pendingEmailAlerts ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  pendingEmailAlerts ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Block Explicit Content</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Prevent explicit songs from being requested
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                // If event is active, show confirmation dialog
                if (eventStatus === 'active') {
                  setPendingBlockExplicitValue(!blockExplicit);
                  setShowContentFilterModal(true);
                } else {
                  setBlockExplicit(!blockExplicit);
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                blockExplicit ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  blockExplicit ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Warn Explicit - only relevant when blocking is off */}
          {!blockExplicit && (
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Warn on Explicit Content</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Show a warning when attendees try to request explicit songs
                </p>
              </div>
              <button
                type="button"
                onClick={() => setWarnExplicit(!warnExplicit)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  warnExplicit ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    warnExplicit ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Auto-Reject Blocked Songs */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Auto-Reject Blocked Songs</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Songs you've blocked in your Song Library will be automatically rejected when requested
              </p>
            </div>
            <button
              onClick={() => setAutoRejectBlocked(!autoRejectBlocked)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                autoRejectBlocked ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  autoRejectBlocked ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Attendee Song Flagging */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Allow Attendee Flagging</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Let attendees flag songs they find inappropriate for your review (max 3 flags per attendee)
              </p>
            </div>
            <button
              onClick={() => setAllowAttendeeFlagging(!allowAttendeeFlagging)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                allowAttendeeFlagging ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  allowAttendeeFlagging ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Vote Rate Limiting (Doom Voting) */}
        <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Vote Rate Limiting</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Prevent spam voting by limiting how fast attendees can vote
              </p>
            </div>
            <button
              onClick={() => setDoomVotingEnabled(!doomVotingEnabled)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                doomVotingEnabled ? 'bg-primary' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  doomVotingEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {doomVotingEnabled && (
            <div className="space-y-4 pl-2 border-l-2 border-primary/20 ml-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Votes in Window
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  How many votes an attendee can cast before the cooldown kicks in
                </p>
                <input
                  type="number"
                  value={doomVotingMaxVotes}
                  onChange={(e) => setDoomVotingMaxVotes(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  max={100}
                  className="input w-24"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Active Window (minutes)
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Time window for counting votes (e.g., 3 min = max votes in any 3 minute period)
                </p>
                <input
                  type="number"
                  value={doomVotingActiveMinutes}
                  onChange={(e) => setDoomVotingActiveMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  max={60}
                  className="input w-24"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rest Period (minutes)
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Cooldown duration when limit is hit — attendee must wait this long before voting again
                </p>
                <input
                  type="number"
                  value={doomVotingRestMinutes}
                  onChange={(e) => setDoomVotingRestMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                  min={1}
                  max={60}
                  className="input w-24"
                />
              </div>
            </div>
          )}
        </div>

        {/* The People's Pick (Super Request) — hidden via feature flag */}
        {!PEOPLES_PICK_HIDDEN && <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">The People's Pick</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                When requests are closed or an attendee hits their request limit, they can still nominate songs. If enough people nominate the same song, it automatically gets added to the queue.
              </p>
            </div>
            <button
              onClick={() => setSuperRequestEnabled(!superRequestEnabled)}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
                superRequestEnabled ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  superRequestEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
          {superRequestEnabled && (
            <div className="space-y-4 pl-2 border-l-2 border-amber-500/20 ml-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Threshold Type
                </label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={superRequestThresholdType === 'count'}
                      onChange={() => setSuperRequestThresholdType('count')}
                      className="accent-amber-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Fixed count</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      checked={superRequestThresholdType === 'percentage'}
                      onChange={() => setSuperRequestThresholdType('percentage')}
                      className="accent-amber-500"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Percentage of attendees</span>
                  </label>
                </div>
              </div>
              {superRequestThresholdType === 'count' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Required nominations
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    How many unique people must nominate the same song
                  </p>
                  <input
                    type="number"
                    value={superRequestThresholdCount}
                    onChange={(e) => setSuperRequestThresholdCount(Math.max(2, parseInt(e.target.value) || 2))}
                    min={2}
                    max={100}
                    className="input w-24"
                  />
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Required percentage (%)
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Percentage of active attendees who must nominate (min 2 people)
                  </p>
                  <input
                    type="number"
                    value={superRequestThresholdPercent}
                    onChange={(e) => setSuperRequestThresholdPercent(Math.max(1, Math.min(100, parseInt(e.target.value) || 1)))}
                    min={1}
                    max={100}
                    className="input w-24"
                  />
                </div>
              )}

              {/* Nomination cap per user */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Nominations Per Attendee
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Limit how many People's Pick nominations a single attendee can submit (0 = unlimited)
                </p>
                <input
                  type="number"
                  value={maxNominationsPerUser}
                  onChange={(e) => setMaxNominationsPerUser(Math.max(0, parseInt(e.target.value) || 0))}
                  min={0}
                  max={100}
                  className="input w-24"
                />
              </div>
            </div>
          )}
        </div>}

        {/* Player (ITER-121) */}
        <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Player
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            The app you play this event's music in. Your buttons open songs there.
          </p>
          <PlayModePicker value={playMode} onChange={setPlayMode} locked={playModeLocked} />
        </div>

        {/* Queue Visibility Settings */}
        <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Queue Visibility
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Control what attendees can see at different stages of your event
          </p>

          {/* Pre-Vote Close Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Before Voting Closes
            </label>
            <select
              value={preEventQueueView}
              onChange={(e) => setPreEventQueueView(e.target.value as 'full' | 'own_only' | 'blind')}
              className="input w-full"
            >
              <option value="full">Full Queue - Attendees can see all songs and votes</option>
              <option value="own_only">Own Requests Only - Attendees see only their own requests</option>
              <option value="blind">Blind Voting - Attendees can browse and vote but can't see vote counts</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {preEventQueueView === 'full' && 'Attendees will see the full queue with all songs and vote counts'}
              {preEventQueueView === 'own_only' && 'Attendees will only see songs they requested'}
              {preEventQueueView === 'blind' && 'Attendees can browse and vote, but vote counts are hidden until voting closes'}
            </p>
          </div>

          {/* Post-Vote Close Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              After Voting Closes
            </label>
            <select
              value={postCloseQueueView}
              onChange={(e) => setPostCloseQueueView(e.target.value as 'show' | 'hide')}
              className="input w-full"
            >
              <option value="show">Show Final Playlist - Reveal the final queue order</option>
              <option value="hide">Hide Until Event - Keep queue hidden until event starts</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {postCloseQueueView === 'show' && 'After voting closes, attendees will see the final playlist order'}
              {postCloseQueueView === 'hide' && 'The queue remains hidden until the event starts (creates anticipation!)'}
            </p>
          </div>

          {/* During Event Visibility */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              During Event
            </label>
            <select
              value={duringEventQueueView}
              onChange={(e) => setDuringEventQueueView(e.target.value as 'now_and_upcoming' | 'now_only' | 'hide')}
              className="input w-full"
            >
              <option value="now_and_upcoming">Now Playing + Upcoming - Show current song and what's coming up</option>
              <option value="now_only">Now Playing Only - Only show the currently playing song</option>
              <option value="hide">Hide All - Don't show any queue information</option>
            </select>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {duringEventQueueView === 'now_and_upcoming' && 'Attendees see the current song and upcoming songs in the queue'}
              {duringEventQueueView === 'now_only' && 'Attendees only see what song is currently playing'}
              {duringEventQueueView === 'hide' && 'The queue is completely hidden during the event'}
            </p>
          </div>

          {/* Hide Ranking Info */}
          <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <Shield className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Hide ranking info from attendees</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Hides vote counts, scores, positions, and trending indicators from the attendee view. You always see full info on the DJ side.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  const newVal = !hideRankingInfo;
                  setHideRankingInfo(newVal);
                  toast.success(newVal ? 'Ranking info will be hidden — save to apply' : 'Ranking info will be visible — save to apply');
                }}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  hideRankingInfo ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    hideRankingInfo ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lottery */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Ticket className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Lottery</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Randomly award prizes to active participants</p>
          </div>
          <button
            type="button"
            onClick={() => {
              const newVal = !lotteryEnabled;
              setLotteryEnabled(newVal);
              toast.success(newVal ? 'Lottery enabled — save to apply' : 'Lottery disabled — save to apply');
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              lotteryEnabled ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                lotteryEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {lotteryEnabled && (
          <div className="space-y-3 ml-12">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Allow repeat winners</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Same attendee can win multiple lottery rounds</p>
              </div>
              <button
                type="button"
                onClick={() => setLotteryAllowRepeatWinners(!lotteryAllowRepeatWinners)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  lotteryAllowRepeatWinners ? 'bg-purple-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    lotteryAllowRepeatWinners ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">
              Configure lottery timing and prizes from the Lottery panel on the queue page
            </p>
          </div>
        )}
      </div>

      {/* Trivia */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
            <Brain className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Music Trivia</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">Prompt for trivia clue curation as songs play</p>
          </div>
          <button
            type="button"
            onClick={() => {
              const newVal = !triviaEnabled;
              setTriviaEnabled(newVal);
              toast.success(newVal ? 'Trivia curation enabled — save to apply' : 'Trivia curation disabled — save to apply');
            }}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              triviaEnabled ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                triviaEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        {triviaEnabled && (
          <p className="text-xs text-gray-500 dark:text-gray-400 italic ml-12">
            When a song moves to Now Playing, you'll be prompted to lock a trivia clue for the previous song. You can also curate from the Played section at any time.
          </p>
        )}
      </div>

      {/* Offline Preparation Checklist */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
            <WifiOff className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Offline Event Preparation
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Complete this checklist before your event if you'll have limited internet access
            </p>
          </div>
        </div>

        {/* Progress indicator */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Progress: {checklistProgress} of {checklistTotal} complete
            </span>
            {checklistProgress === checklistTotal && (
              <span className="flex items-center gap-1 text-sm text-success font-medium">
                <CheckCircle className="w-4 h-4" />
                Ready for offline!
              </span>
            )}
          </div>
          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
            <div
              className={`h-full transition-all duration-300 ${
                checklistProgress === checklistTotal
                  ? 'bg-success'
                  : checklistProgress > checklistTotal / 2
                  ? 'bg-amber-500'
                  : 'bg-primary'
              }`}
              style={{ width: `${(checklistProgress / checklistTotal) * 100}%` }}
            />
          </div>
        </div>

        {/* Checklist items */}
        <div className="space-y-2">
          {offlineChecklist.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => toggleChecklistItem(item.id)}
              className={`w-full flex items-start gap-3 p-3 rounded-lg border transition-colors text-left ${
                item.checked
                  ? 'bg-success/5 border-success/20 hover:bg-success/10'
                  : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              <div className={`mt-0.5 flex-shrink-0 ${item.checked ? 'text-success' : 'text-gray-400'}`}>
                {item.checked ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <Square className="w-5 h-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${
                  item.checked
                    ? 'text-success line-through'
                    : 'text-gray-900 dark:text-white'
                }`}>
                  {item.label}
                </p>
                <p className={`text-sm ${
                  item.checked
                    ? 'text-success/70'
                    : 'text-gray-600 dark:text-gray-400'
                }`}>
                  {item.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Import from Past Event */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-gray-900 dark:text-white">Import from Past Event</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Import songs from another event into this queue with Spotify matches preserved
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowLibraryModal(true)}
              className="btn btn-secondary"
            >
              Add from Library
            </button>
            <button
              type="button"
              onClick={handleOpenImportModal}
              className="btn btn-secondary"
            >
              <Download className="w-5 h-5 mr-2" />
              Import Songs
            </button>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setShowDeleteModal(true)}
            className="btn text-danger hover:bg-danger/10"
          >
            <Trash2 className="w-5 h-5 mr-2" />
            Delete Event
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="btn btn-secondary"
          >
            <RotateCcw className="w-5 h-5 mr-2" />
            Reset to Defaults
          </button>
          <button
            type="button"
            onClick={() => setShowSaveTemplateModal(true)}
            className="btn btn-secondary"
          >
            <FileUp className="w-5 h-5 mr-2" />
            Save as Template
          </button>
          <button
            type="button"
            onClick={handleSaveAsDefault}
            className="btn btn-secondary"
          >
            <Star className="w-5 h-5 mr-2" />
            Save as Default
          </button>
        </div>
        <button
          type="button"
          onClick={async () => { await handleSubmit(); if (event) navigate(`/events/${event.id}/manage/queue`); }}
          className="btn bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
          disabled={saveLoading}
        >
          <ListMusic className="w-5 h-5 mr-2" />
          Save & Go to Queue
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          className="btn btn-primary"
          disabled={saveLoading}
        >
          {saveLoading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5 mr-2" />
              Save Changes
            </>
          )}
        </button>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-danger" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete Event
                </h3>
              </div>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Are you sure you want to delete <strong>"{event?.name}"</strong>?
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
              This removes the event, its song requests and voting history from your account. This can&apos;t be undone.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary flex-1"
                disabled={deleteLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteEvent}
                className="btn bg-danger text-white hover:bg-red-600 flex-1"
                disabled={deleteLoading}
              >
                {deleteLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5 mr-2" />
                    Delete Event
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Voting Confirmation Modal */}
      {showCloseVotingModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Close Voting?
                </h3>
              </div>
              <button
                onClick={() => setShowCloseVotingModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Are you sure you want to close voting for <strong>"{event?.name}"</strong>?
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
              Attendees will no longer be able to submit new song requests or vote on existing ones. You can reopen voting at any time.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowCloseVotingModal(false)}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  setIsActive(false);
                  // Also set votingClosed flag with timestamp
                  if (event) {
                    try {
                      await updateEvent(event.id, {
                        settings: {
                          ...event.settings,
                          requestsOpen: false,
                          votingClosed: true,
                          votingClosedAt: new Date().toISOString(),
                        },
                      });
                    } catch {
                      // Settings will be saved when user clicks Save Changes
                    }
                  }
                  setShowCloseVotingModal(false);
                  toast.success('Voting closed - attendees can no longer submit or vote');
                }}
                className="btn bg-amber-500 text-white hover:bg-amber-600 flex-1"
              >
                <WifiOff className="w-5 h-5 mr-2" />
                Close Voting
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Filter Change Confirmation Modal */}
      {showContentFilterModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Change Content Filter?
                </h3>
              </div>
              <button
                onClick={() => setShowContentFilterModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-2">
              {pendingBlockExplicitValue
                ? 'Are you sure you want to enable explicit content blocking?'
                : 'Are you sure you want to disable explicit content blocking?'}
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
              {pendingBlockExplicitValue
                ? 'This will prevent any new explicit songs from being requested. Existing explicit songs in the queue will remain.'
                : 'This will allow attendees to request explicit songs during your active event.'}
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowContentFilterModal(false)}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  setBlockExplicit(pendingBlockExplicitValue);
                  setShowContentFilterModal(false);
                  toast.success(
                    pendingBlockExplicitValue
                      ? 'Explicit content blocking enabled'
                      : 'Explicit content blocking disabled'
                  );
                }}
                className="btn bg-amber-500 text-white hover:bg-amber-600 flex-1"
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save as Template Modal */}
      {showSaveTemplateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <FileUp className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Save as Template
                </h3>
              </div>
              <button
                onClick={() => setShowSaveTemplateModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Save current event settings as a template to reuse when creating future events.
            </p>

            <div className="mb-6">
              <label htmlFor="templateName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Template Name
              </label>
              <input
                id="templateName"
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="e.g., Wedding Reception, Club Night"
                className="input w-full"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowSaveTemplateModal(false)}
                className="btn btn-secondary flex-1"
                disabled={templateSaveLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveAsTemplate}
                className="btn btn-primary flex-1"
                disabled={templateSaveLoading || !templateName.trim()}
              >
                {templateSaveLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5 mr-2" />
                    Save Template
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import from Past Event Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-lg w-full mx-4 p-6 shadow-2xl max-h-[80vh] flex flex-col">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                {importStep === 'pick-songs' && !importResult && (
                  <button
                    onClick={() => { setImportStep('pick-event'); setSelectedPastEvent(null); setPastEventSongs([]); setSelectedSongIds(new Set()); }}
                    className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Download className="w-5 h-5 text-primary" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {importResult ? 'Import Complete' : importStep === 'pick-event' ? 'Import from Past Event' : `Import from: ${selectedPastEvent?.name}`}
                </h3>
              </div>
              <button
                onClick={handleCloseImportModal}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                disabled={importing}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Step 1: Pick Event */}
            {importStep === 'pick-event' && !importResult && (
              <>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Select an event to import songs from.
                </p>
                <div className="flex-1 overflow-y-auto space-y-2">
                  {pastEventsLoading ? (
                    <div className="text-center py-8">
                      <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
                      <p className="text-gray-500">Loading events...</p>
                    </div>
                  ) : pastEvents.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">No other events found.</p>
                  ) : (
                    pastEvents.map(pe => (
                      <button
                        key={pe.id}
                        type="button"
                        onClick={() => handleSelectPastEvent(pe)}
                        className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-left transition-colors"
                      >
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{pe.name}</p>
                          <p className="text-sm text-gray-500">{pe.date} &middot; {pe.status}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </button>
                    ))
                  )}
                </div>
              </>
            )}

            {/* Step 2: Pick Songs */}
            {importStep === 'pick-songs' && !importResult && (
              <>
                {pastSongsLoading ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-2" />
                    <p className="text-gray-500">Loading songs...</p>
                  </div>
                ) : pastEventSongs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No queued or played songs in this event.</p>
                ) : (
                  <>
                    {/* Select all / count */}
                    <div className="flex items-center justify-between mb-3">
                      <button type="button" onClick={toggleSelectAll} className="text-sm text-primary hover:underline">
                        {selectedSongIds.size === pastEventSongs.length ? 'Deselect All' : 'Select All'}
                      </button>
                      <span className="text-sm text-gray-500">
                        {selectedSongIds.size} of {pastEventSongs.length} selected
                      </span>
                    </div>
                    {/* Song list */}
                    <div className="flex-1 overflow-y-auto space-y-1 mb-4">
                      {pastEventSongs.map(song => (
                        <button
                          key={song.id}
                          type="button"
                          onClick={() => toggleSongSelection(song.id)}
                          className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-colors text-left ${
                            selectedSongIds.has(song.id)
                              ? 'bg-primary/5 border-primary/20'
                              : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
                          }`}
                        >
                          <div className={`flex-shrink-0 ${selectedSongIds.has(song.id) ? 'text-primary' : 'text-gray-400'}`}>
                            {selectedSongIds.has(song.id) ? <Check className="w-5 h-5" /> : <Square className="w-5 h-5" />}
                          </div>
                          {song.song.albumArtUrl && (
                            <img src={song.song.albumArtUrl} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 dark:text-white truncate">{song.song.title}</p>
                            <p className="text-sm text-gray-500 truncate">{song.song.artist}</p>
                          </div>
                          {partitionForImport([song], savedPlayMode).fits.length > 0 ? (
                            <span title="Matched — will go to Queue">
                              <Music className="w-4 h-4 text-green-500 flex-shrink-0" />
                            </span>
                          ) : (
                            <span className="text-xs font-medium text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded-full flex-shrink-0" title="Will go to Pending for a match">
                              No {savedPlayMode.playerName} match
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                    {/* Smart routing info */}
                    <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
                      <Music className="w-3.5 h-3.5 text-green-500 inline mr-1" />Matched songs go to Queue &middot;
                      <Clock className="w-3.5 h-3.5 text-amber-500 inline ml-2 mr-1" />Songs with no {savedPlayMode.playerName} match go to Pending
                    </p>
                    {!playModeLocked && playMode === savedPlayMode.id && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 pb-2">
                        Importing locks this event's player to {savedPlayMode.playerName}.
                      </p>
                    )}
                    {playMode !== savedPlayMode.id && (
                      <p className="text-xs text-amber-700 dark:text-amber-400 pb-2">
                        Save your player change before importing. Imported songs lock the event to its saved player, {savedPlayMode.playerName}.
                      </p>
                    )}
                    {/* Import button */}
                    <button
                      type="button"
                      onClick={handleImportSongs}
                      className="btn btn-primary w-full"
                      disabled={importing || selectedSongIds.size === 0 || playMode !== savedPlayMode.id}
                    >
                      {importing ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Importing {selectedSongIds.size} song{selectedSongIds.size !== 1 ? 's' : ''}...
                        </>
                      ) : (
                        <>
                          <Download className="w-5 h-5 mr-2" />
                          Import {selectedSongIds.size} Songs
                        </>
                      )}
                    </button>
                  </>
                )}
              </>
            )}

            {/* Result state */}
            {importResult && (
              <div className="py-4">
                <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg mb-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-700 dark:text-green-300">
                        Import complete!
                      </p>
                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                        {importResult.importedToQueue > 0 && <>{importResult.importedToQueue} to Queue</>}
                        {importResult.importedToQueue > 0 && importResult.importedToPending > 0 && ', '}
                        {importResult.importedToPending > 0 && <>{importResult.importedToPending} to Pending</>}
                        {importResult.skippedDuplicates > 0 && `, ${importResult.skippedDuplicates} duplicate${importResult.skippedDuplicates !== 1 ? 's' : ''} skipped`}
                      </p>
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleCloseImportModal}
                  className="btn btn-secondary w-full"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR Code Fullscreen Modal */}
      {showQrModal && qrCodeUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowQrModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl max-w-lg w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Event QR Code
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Scan to join event
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowQrModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex justify-center mb-4">
              <div className="bg-white p-4 rounded-lg shadow-inner">
                <img
                  src={qrCodeUrl}
                  alt="Event QR Code"
                  className="w-64 h-64 object-contain"
                />
              </div>
            </div>

            <p className="text-center text-sm text-gray-600 dark:text-gray-400 mb-4">
              {eventShareUrl}
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowQrModal(false)}
                className="btn btn-secondary flex-1"
              >
                Close
              </button>
              <button
                onClick={() => { setShowQrModal(false); setShowQrPrint(true); }}
                className="btn btn-primary flex-1 flex items-center justify-center gap-2"
              >
                <Printer className="w-4 h-4" />
                Print
              </button>
              <button
                onClick={() => {
                  handleDownloadQrCode();
                  setShowQrModal(false);
                }}
                className="btn btn-secondary flex-1 flex items-center justify-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download PNG
              </button>
            </div>
            {nfcSupported && (
              <div className="mt-3">
                {nfcWriting && nfcWritingType === 'event' ? (
                  <div className="space-y-2">
                    <div className="w-full btn bg-blue-500 text-white flex items-center justify-center gap-2 animate-pulse cursor-default">
                      <Smartphone className="w-4 h-4" />
                      Hold tag to phone...
                    </div>
                    <button onClick={handleCancelNfc} className="w-full btn btn-secondary text-sm">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => handleWriteNfc(eventShareUrl, 'event')}
                      disabled={nfcWriting}
                      className="w-full btn bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center gap-2"
                    >
                      <Smartphone className="w-4 h-4" />
                      Write NFC Tag
                    </button>
                    <label className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={nfcLockTag}
                        onChange={(e) => setNfcLockTag(e.target.checked)}
                        className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                      />
                      <span>Lock tag after writing <span className="text-amber-500">(permanent — cannot be rewritten)</span></span>
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Enable On-Site Verification Confirmation Modal */}
      {showEnableVerificationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-teal-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Enable On-Site Verification?
                </h3>
              </div>
              <button
                onClick={() => setShowEnableVerificationModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-2">
              A unique QR code will be generated for you to display at the venue.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-2 text-sm">
              Attendees must scan this QR code to request songs, vote, or rank. Anyone who doesn't scan will only be able to view the queue.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
              Existing requests and votes will be kept. You can disable this at any time to restore full access for everyone.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowEnableVerificationModal(false)}
                className="btn btn-secondary flex-1"
                disabled={verificationLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleEnableVerification}
                className="btn bg-teal-500 text-white hover:bg-teal-600 flex-1"
                disabled={verificationLoading}
              >
                {verificationLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Enabling...
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5 mr-2" />
                    Enable
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Regenerate Verification Token Confirmation Modal */}
      {showRegenerateTokenModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 shadow-2xl">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center">
                  <RefreshCw className="w-5 h-5 text-amber-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Regenerate Verification Code?
                </h3>
              </div>
              <button
                onClick={() => setShowRegenerateTokenModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-gray-600 dark:text-gray-400 mb-2">
              This will create a new verification QR code and <strong>revoke all existing verifications</strong>.
            </p>
            <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm">
              All {verifiedCount} currently verified attendee{verifiedCount !== 1 ? 's' : ''} will need to scan the new QR code again. Use this if you suspect the code has been shared outside the venue.
            </p>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowRegenerateTokenModal(false)}
                className="btn btn-secondary flex-1"
                disabled={verificationLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRegenerateToken}
                className="btn bg-amber-500 text-white hover:bg-amber-600 flex-1"
                disabled={verificationLoading}
              >
                {verificationLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Regenerating...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2" />
                    Regenerate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Verification QR Code Fullscreen Modal */}
      {showVerificationQrModal && verificationQrUrl && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowVerificationQrModal(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl max-w-lg w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-teal-100 dark:bg-teal-900/30 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-teal-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    On-Site Verification QR
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Scan to verify attendance
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowVerificationQrModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex justify-center mb-4">
              <div className="bg-white p-4 rounded-lg shadow-inner border-2 border-teal-200">
                <img
                  src={verificationQrUrl}
                  alt="Verification QR Code"
                  className="w-64 h-64 object-contain"
                />
              </div>
            </div>

            <p className="text-center text-sm text-gray-600 dark:text-gray-400 mb-2">
              Display this at the venue for attendees to scan
            </p>
            <p className="text-center text-xs text-teal-600 dark:text-teal-400 mb-4 flex items-center justify-center gap-1">
              <Users className="w-3 h-3" />
              {verifiedCount} verified
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setShowVerificationQrModal(false)}
                className="btn btn-secondary flex-1"
              >
                Close
              </button>
              <button
                onClick={() => {
                  handleDownloadVerificationQr();
                  setShowVerificationQrModal(false);
                }}
                className="btn bg-teal-500 text-white hover:bg-teal-600 flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PNG
              </button>
            </div>
            {nfcSupported && (
              <div className="mt-3">
                {nfcWriting && nfcWritingType === 'verification' ? (
                  <div className="space-y-2">
                    <div className="w-full btn bg-blue-500 text-white flex items-center justify-center gap-2 animate-pulse cursor-default">
                      <Smartphone className="w-4 h-4" />
                      Hold tag to phone...
                    </div>
                    <button onClick={handleCancelNfc} className="w-full btn btn-secondary text-sm">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={() => handleWriteNfc(verificationShareUrl, 'verification')}
                      disabled={nfcWriting}
                      className="w-full btn bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center gap-2"
                    >
                      <Smartphone className="w-4 h-4" />
                      Write NFC Tag
                    </button>
                    <label className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={nfcLockTag}
                        onChange={(e) => setNfcLockTag(e.target.checked)}
                        className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                      />
                      <span>Lock tag after writing <span className="text-amber-500">(permanent — cannot be rewritten)</span></span>
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      </fieldset>

      {/* QR Print Modal */}
      {showQrPrint && event && (
        <QrPrintModal
          eventName={event.name}
          eventDate={event.date}
          startTime={event.startTime}
          endTime={event.endTime}
          eventUrl={event.eventUrl}
          location={event.location}
          address={event.address}
          contactInfo={event.contactInfo}
          onClose={() => setShowQrPrint(false)}
        />
      )}

      {/* Floating Save Bar — appears when there are unsaved changes */}
      {!isLocked && <SaveBar
        hasChanges={hasUnsavedChanges}
        saving={saveLoading}
        onSave={handleSubmit}
        onDiscard={handleDiscardChanges}
        onSaveAndNavigate={async () => { await handleSubmit(); if (event) navigate(`/events/${event.id}/manage/queue`); }}
      />}

      {/* Unsaved Changes Warning Modal */}
      <UnsavedChangesModal
        isOpen={showUnsavedModal}
        onCancel={cancelNavigation}
        onProceed={proceedNavigation}
        message="You have unsaved changes to your event settings. Are you sure you want to leave without saving?"
      />

      {/* Library Queue Modal */}
      {showLibraryModal && event && user && (
        <LibraryQueueModal
          djUid={user.uid}
          eventId={event.id}
          existingRequests={[]}
          playMode={savedPlayMode}
          onSongsAdded={() => { setShowLibraryModal(false); toast.success('Songs added to queue'); }}
          onClose={() => setShowLibraryModal(false)}
        />
      )}
    </div>
  );
}
